import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { formatCurrency } from '@/lib/i18n';
import { handle, handleWithFallback } from '@/lib/displayName';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import StripeOnboardingConfirmDialog from '@/components/StripeOnboardingConfirmDialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import VerifiedBadge from '@/components/VerifiedBadge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { io } from 'socket.io-client';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import Navbar from '@/components/layout/Navbar';
import WeatherWidget from '@/components/WeatherWidget';
import PurchaseConfirmDialog from '@/components/PurchaseConfirmDialog';
import EventPayReconcilePanel from '@/components/event-payment/EventPayReconcilePanel';
import EventPayReconcileOrganiserPanel from '@/components/event-payment/EventPayReconcileOrganiserPanel';
import { useChat } from '@/context/ChatContext';
import { EventSEO } from '@/components/SEO';
import { 
  MapPin, 
  Calendar, 
  Car,
  Star,
  Clock,
  Users,
  ArrowLeft,
  Phone,
  MessageCircle,
  Loader2,
  ChevronRight,
  BadgeCheck,
  Mail,
  Shield,
  Pencil,
  Trash2,
  CalendarIcon,
  Upload,
  X,
  Plus,
  ParkingCircle,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Share2,
  ScanLine,
  ExternalLink,
  CheckCircle,
  UserPlus,
  Navigation,
  Instagram
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Promo Codes Management Component
const PromoCodesSection = ({ eventId }) => {
  const [codes, setCodes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchCodes = async () => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/promo-codes/${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
      setCodes(res.data);
    } catch {}
  };

  useEffect(() => { fetchCodes(); }, [eventId]);

  const handleCreate = async () => {
    if (!newCode.trim() || !discountValue) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.post(`${API}/promo-codes`, {
        event_id: eventId,
        code: newCode.trim(),
        discount_type: discountType,
        discount_value: parseInt(discountValue),
        max_uses: maxUses ? parseInt(maxUses) : null,
      }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      toast.success('Promo code created!');
      setNewCode(''); setDiscountValue(''); setMaxUses(''); setShowForm(false);
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create code');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (promoId) => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.delete(`${API}/promo-codes/${promoId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Code deleted');
      fetchCodes();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-border/30">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-foreground flex items-center gap-2">
          <svg className="w-4 h-4 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
          Promo Codes
        </p>
        <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => setShowForm(!showForm)} data-testid="add-promo-btn">
          {showForm ? 'Cancel' : '+ Add Code'}
        </Button>
      </div>

      {showForm && (
        <div className="space-y-2 mb-3 p-3 rounded-xl bg-muted/20 border border-border/30">
          <Input placeholder="Code e.g. SKATE20" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} className="h-8 text-sm uppercase" data-testid="promo-new-code" />
          <div className="flex gap-2">
            <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} className="h-8 rounded-lg bg-background border border-border text-sm px-2 flex-1">
              <option value="percent">% Off</option>
              <option value="fixed">£ Off (pence)</option>
            </select>
            <Input type="number" placeholder={discountType === 'percent' ? '20' : '500'} value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="h-8 text-sm flex-1" data-testid="promo-discount-value" />
          </div>
          <Input type="number" placeholder="Max uses (optional)" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} className="h-8 text-sm" />
          <Button size="sm" className="rounded-full w-full" onClick={handleCreate} disabled={loading} data-testid="promo-create-btn">
            {loading ? 'Creating...' : 'Create Code'}
          </Button>
        </div>
      )}

      {codes.length === 0 && !showForm ? (
        <p className="text-xs text-muted-foreground">No promo codes yet. Create one to offer discounts.</p>
      ) : (
        <div className="space-y-1.5">
          {codes.map((c) => (
            <div key={c.promo_id} className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-muted/10 hover:bg-muted/20 transition-colors">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">{c.code}</Badge>
                <span className="text-xs text-muted-foreground">
                  {c.discount_type === 'percent' ? `${c.discount_value}% off` : `£${(c.discount_value / 100).toFixed(2)} off`}
                </span>
                <span className="text-xs text-muted-foreground/60">
                  {c.times_used} used{c.max_uses ? `/${c.max_uses}` : ''}
                </span>
              </div>
              <button onClick={() => handleDelete(c.promo_id)} className="text-muted-foreground hover:text-destructive transition-colors p-1" data-testid={`promo-delete-${c.promo_id}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EventDetailsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { openChat, activeChat } = useChat();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedPickup, setSelectedPickup] = useState(0);
  const [isReturnTrip, setIsReturnTrip] = useState(false);
  // Which leg of the ride the rider is booking. round_trip = both legs
  // (full price), outbound/return = one leg only at half price.
  const [selectedLeg, setSelectedLeg] = useState('round_trip');
  const [tripAvailability, setTripAvailability] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [pickupConfirmed, setPickupConfirmed] = useState(false);
  
  // Edit/Delete state
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [showRefundInfo, setShowRefundInfo] = useState(false);
  
  // Ticket purchase state
  const [ticketDialog, setTicketDialog] = useState(false);
  const [ticketQuantity, setTicketQuantity] = useState(1);  const [selectedReleaseIndex, setSelectedReleaseIndex] = useState(-1);
  const [feeConfig, setFeeConfig] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [purchaseConfirm, setPurchaseConfirm] = useState({ open: false, eventName: '', ticketCount: 1, paymentType: 'free' });
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [organiserStripeStatus, setOrganiserStripeStatus] = useState(null);
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);
  const [showOrganiserStripeConfirm, setShowOrganiserStripeConfirm] = useState(false);

  const startOrganiserStripeOnboarding = async () => {
    setStripeConnectLoading(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const { data } = await axios.post(`${API}/drivers/stripe-connect/onboard`, {
        return_url: `${window.location.origin}/events/${eventId}?stripe_return=true`,
        refresh_url: `${window.location.origin}/events/${eventId}?stripe_refresh=true`
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.already_onboarded) {
        toast.success('Stripe already connected!');
        setOrganiserStripeStatus({ status: 'complete' });
        setShowOrganiserStripeConfirm(false);
      } else if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      }
    } catch { toast.error('Failed to start Stripe setup'); }
    finally { setStripeConnectLoading(false); }
  };
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [flyerUploading, setFlyerUploading] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const flyerInputRef = React.useRef(null);
  const thumbnailInputRef = React.useRef(null);
  const thumbnailPreviewRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    date: null,
    time: '19:00',
    location: { address: '', lat: 51.5074, lng: -0.1278 },
    image_url: '',
    flyer_url: '',
    ticket_link: '',
    tickets_enabled: false,
    ticket_price: '',
    ticket_quantity: '',
    max_per_buyer: '6',
    has_onsite_parking: false,
    alternative_parking: '',
    requires_id: false,
    has_bar: false,
    has_food_vendors: false,
    has_toilets: false,
    has_skate_hire: false,
    nearest_stations: [''],
    thumbnail_position: { x: 50, y: 50, scale: 1 },
    booking_fee_override: false,
    booking_fee_percent: '',
    booking_fee_fixed_pence: '',
    fee_passed_to_buyer: true,
    ticket_releases: []
  });

  // Ride request / waitlist state
  const [rideRequests, setRideRequests] = useState([]);
  const [myRideRequest, setMyRideRequest] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [goingCount, setGoingCount] = useState(0);
  const [imGoing, setImGoing] = useState(false);
  const [goingLoading, setGoingLoading] = useState(false);
  const [requestDialog, setRequestDialog] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestForm, setRequestForm] = useState({
    pickup_locations: [''],
    notes: '',
    passenger_count: 1,
    preferred_date: '',
    preferred_time: '',
    leg: 'round_trip',
  });
  const [requestStep, setRequestStep] = useState(0);
  const [acceptDialog, setAcceptDialog] = useState(false);
  const [acceptedRiderName, setAcceptedRiderName] = useState('');
  const [showAcceptSuccess, setShowAcceptSuccess] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedTripForAccept, setSelectedTripForAccept] = useState('');
  const [selectedPickupForAccept, setSelectedPickupForAccept] = useState('');
  const [checkedPickupsForTrip, setCheckedPickupsForTrip] = useState([]);
  const [tripPassengers, setTripPassengers] = useState({});

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  
  // Attendee list + analytics state
  const [attendeeDialog, setAttendeeDialog] = useState(false);
  const [attendeeList, setAttendeeList] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);

  // Civilian Nights — organiser reconciliation list. Populated only when
  // the current viewer is the organiser AND the event has
  // organiser_collects_money enabled.
  const [reconcileAttendees, setReconcileAttendees] = useState([]);
  const fetchReconcileAttendees = useCallback(async () => {
    if (!eventId) return;
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/events/${eventId}/attendees/reconcile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReconcileAttendees(res.data?.attendees || []);
    } catch {
      // 403 (non-organiser) or other — keep list empty.
    }
  }, [eventId]);

  const socketRef = useRef(null);

  const fetchEventSilent = useCallback(async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.get(`${API}/events/${eventId}`, { headers });
      const data = res.data;
      const normalized = {
        ...data,
        name: data.name || data.title || 'Untitled Event',
        location: data.location || {
          address: data.venue_address || data.venue_name || '',
          lat: data.lat || data.venue_lat || 51.5074,
          lng: data.lng || data.venue_lng || -0.1278
        },
        flyer_url: data.flyer_url || '',
        has_onsite_parking: data.has_onsite_parking || false,
        alternative_parking: data.alternative_parking || '',
        thumbnail_position: data.thumbnail_position || { x: 50, y: 50, scale: 1 },
      };
      setEvent(normalized);
    } catch (err) {
      // silent refresh failure
    }
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
    if (user) {
      fetchRideRequests();
    }
    // Fetch attendees
    axios.get(`${API}/events/${eventId}/attendees`)
      .then(res => {
        setAttendees(res.data?.attendees || []);
        setGoingCount(res.data?.going_count || 0);
      })
      .catch(() => {});

    // Check if current user is marked as going
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/events/${eventId}/going/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setImGoing(res.data?.going || false))
        .catch(() => {});
      // Fetch waitlist status
      axios.get(`${API}/events/${eventId}/waitlist/status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setWaitlistStatus(res.data))
        .catch(() => {});
    }

    // Connect to socket.io for real-time trip updates
    const socket = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      socket.emit('join_event_trips', { event_id: eventId });
    });

    socket.on('trip_update', (data) => {
      if (data.event_id === eventId) {
        fetchEventSilent();
        if (user) fetchRideRequests();
      }
    });

    socketRef.current = socket;

    // Fallback polling every 60s in case socket disconnects
    const fallbackInterval = setInterval(() => {
      if (!socketRef.current?.connected) {
        fetchEventSilent();
      }
    }, 60000);

    return () => {
      clearInterval(fallbackInterval);
      if (socketRef.current) {
        socketRef.current.emit('leave_event_trips', { event_id: eventId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [eventId, user, fetchEventSilent]);

  // Permanent venues (e.g. Snow Centre) are open daily — they never "end" and
  // their stored sentinel date (e.g. 2099-12-31) must never be displayed.
  const isVenue = event?.venue_type === 'venue';
  const isEventEnded = (() => {
    if (!event?.date || isVenue) return false;
    const now = new Date(new Date().setHours(0,0,0,0));
    const allDates = [event.date, ...(event.additional_dates || [])];
    const latest = allDates.reduce((a, b) => a > b ? a : b);
    return new Date(latest) < now;
  })();
  const handleOpenChat = () => {
    if (!user) {
      toast.error('Please login to join the chat');
      navigate('/login', { state: { from: `/events/${eventId}` } });
      return;
    }
    if (isEventEnded) {
      toast('Chat is no longer available for past events');
      return;
    }
    openChat(eventId, event.name, event.image_url || event.thumbnail_url || event.flyer_url || null);
  };

  // Fetch passengers for driver's trips
  useEffect(() => {
    if (event?.trips && user) {
      const myTrips = event.trips.filter(t => t.driver_id === user.user_id);
      myTrips.forEach(trip => {
        fetchTripPassengers(trip.trip_id);
      });
    }
  }, [event, user]);

  // Populate edit form when event loads
  useEffect(() => {
    if (event) {
      let eventDate;
      try {
        if (event.date && event.time) {
          eventDate = new Date(`${event.date}T${event.time}`);
        } else {
          eventDate = new Date(event.date);
        }
        if (isNaN(eventDate.getTime())) eventDate = new Date();
      } catch { eventDate = new Date(); }

      let timeStr;
      try {
        timeStr = event.time || format(eventDate, 'HH:mm');
      } catch { timeStr = '19:00'; }

      setEditForm({
        name: event.name || '',
        description: event.description || '',
        date: eventDate,
        time: timeStr,
        location: event.location || { address: '', lat: 51.5074, lng: -0.1278 },
        image_url: event.image_url || '',
        flyer_url: event.flyer_url || '',
        ticket_link: event.ticket_link || '',
        tickets_enabled: event.tickets_enabled || false,
        ticket_price: event.ticket_price_pence ? (event.ticket_price_pence / 100).toFixed(2) : '',
        ticket_quantity: event.ticket_quantity ? String(event.ticket_quantity) : '',
        max_per_buyer: event.max_per_buyer ? String(event.max_per_buyer) : '6',
        has_onsite_parking: event.has_onsite_parking || false,
        alternative_parking: event.alternative_parking || '',
        requires_id: event.requires_id || false,
        has_bar: event.has_bar || false,
        has_food_vendors: event.has_food_vendors || false,
        has_toilets: event.has_toilets || false,
        has_skate_hire: event.has_skate_hire || false,
        nearest_stations: event.nearest_stations?.length ? [...event.nearest_stations] : [''],
        thumbnail_position: event.thumbnail_position || { x: 50, y: 50, scale: 1 },
        booking_fee_override: event.booking_fee_percent != null,
        booking_fee_percent: event.booking_fee_percent != null ? String(event.booking_fee_percent) : '',
        booking_fee_fixed_pence: event.booking_fee_fixed_pence != null ? String(event.booking_fee_fixed_pence) : '',
        fee_passed_to_buyer: event.fee_passed_to_buyer != null ? event.fee_passed_to_buyer : true,
        ticket_releases: (event.ticket_releases || []).map(r => ({
          name: r.name || '',
          price: r.price_pence ? (r.price_pence / 100).toFixed(2) : '',
          quantity: r.quantity ? String(r.quantity) : '',
          end_date: r.end_date ? r.end_date.split('T')[0] : '',
          sold: r.sold || 0
        }))
      });
    }
  }, [event]);

  // Auto-open accept dialog when returning from trip creation
  const autoOpenHandled = useRef(false);
  useEffect(() => {
    if (autoOpenHandled.current) return;
    if (location.state?.acceptRequestId && rideRequests.length > 0 && event?.trips?.length > 0) {
      const request = rideRequests.find(r => r.request_id === location.state.acceptRequestId && r.status === 'waiting');
      if (request) {
        autoOpenHandled.current = true;
        setSelectedRequest(request);
        setSelectedPickupForAccept('');
        setCheckedPickupsForTrip([]);
        setAcceptDialog(true);
        // Replace state to prevent re-trigger on page refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, rideRequests, event, navigate, location.pathname]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/events/${eventId}`);
      const data = response.data;
      // Normalize API fields to component expectations
      const normalized = {
        ...data,
        name: data.name || data.title || 'Untitled Event',
        location: data.location || {
          address: data.venue_address || data.venue_name || '',
          lat: data.lat || data.venue_lat || 51.5074,
          lng: data.lng || data.venue_lng || -0.1278
        },
        flyer_url: data.flyer_url || '',
        has_onsite_parking: data.has_onsite_parking || false,
        alternative_parking: data.alternative_parking || '',
        thumbnail_position: data.thumbnail_position || { x: 50, y: 50, scale: 1 },
      };
      setEvent(normalized);
      // Track event view
      axios.post(`${API}/events/${eventId}/view`).catch(() => {});
      // Fetch fee config for ticket-enabled events (needed for release info)
      if (normalized.tickets_enabled) {
        axios.get(`${API}/ticket-fee-config/${eventId}`).then(res => setFeeConfig(res.data)).catch(() => {});
      }
      // Check organiser's Stripe status
      if (user && (user.user_id === response.data.organiser_id || user.role === 'admin')) {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        if (token) {
          axios.get(`${API}/drivers/stripe-connect/status`, { headers: { Authorization: `Bearer ${token}` } })
            .then(({ data }) => setOrganiserStripeStatus(data))
            .catch(() => {});
        }
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Event not found');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const fetchRideRequests = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/ride-requests`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` }
      });
      setRideRequests(response.data.requests || []);
      
      // Find if current user has a request
      const myRequest = response.data.requests?.find(r => r.rider_id === user?.user_id);
      setMyRideRequest(myRequest || null);
    } catch (error) {
      console.error('Error fetching ride requests:', error);
    }
  };

  const fetchTripPassengers = async (tripId) => {
    try {
      const response = await axios.get(`${API}/trips/${tripId}/passengers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` }
      });
      setTripPassengers(prev => ({ ...prev, [tripId]: response.data.passengers || [] }));
    } catch (error) {
      // Not the driver — ignore 403
    }
  };

  const handleRequestRide = async () => {
    const validLocations = requestForm.pickup_locations.filter(l => l.trim());
    if (validLocations.length === 0) {
      toast.error('Please add at least one pickup location');
      return;
    }
    if (isVenue && !requestForm.preferred_date) {
      toast.error('Please pick the date you want to visit this venue');
      return;
    }
    
    setRequestLoading(true);
    try {
      // Join locations with newline + bullet for backend storage
      const pickupArea = validLocations.map(l => `• ${l.trim()}`).join('\n');
      
      await axios.post(`${API}/events/${eventId}/request-ride`, {
        event_id: eventId,
        pickup_area: pickupArea,
        notes: requestForm.notes,
        passenger_count: requestForm.passenger_count,
        preferred_date: isVenue ? requestForm.preferred_date : undefined,
        preferred_time: isVenue ? (requestForm.preferred_time || undefined) : undefined,
        leg: requestForm.leg || 'round_trip',
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` }
      });
      
      toast.success('Ride request submitted! Drivers will see your request.');
      setRequestDialog(false);
      setRequestStep(0);
      setRequestForm({ pickup_locations: [''], notes: '', passenger_count: 1, preferred_date: '', preferred_time: '', leg: 'round_trip' });
      fetchRideRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!myRideRequest) return;
    
    try {
      await axios.delete(`${API}/ride-requests/${myRideRequest.request_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` }
      });
      toast.success('Ride request cancelled');
      setMyRideRequest(null);
      fetchRideRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const handleAcceptRequest = async () => {
    if (!selectedRequest || !selectedTripForAccept) {
      toast.error('Please select a trip');
      return;
    }

    const locations = parsePickupLocations(selectedRequest.pickup_area);
    if (locations.length > 1 && !selectedPickupForAccept) {
      toast.error('Please select a pickup location');
      return;
    }
    
    try {
      const pickupParam = selectedPickupForAccept ? `&pickup_location=${encodeURIComponent(selectedPickupForAccept)}` : '';
      await axios.put(`${API}/ride-requests/${selectedRequest.request_id}/accept?trip_id=${selectedTripForAccept}${pickupParam}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` }
      });
      
      setAcceptedRiderName(handle(selectedRequest, 'rider'));
      setAcceptDialog(false);
      setSelectedRequest(null);
      setSelectedTripForAccept('');
      setSelectedPickupForAccept('');
      setCheckedPickupsForTrip([]);
      setShowAcceptSuccess(true);
      fetchRideRequests();
      fetchEvent(); // Refresh to update seat counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept request');
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '';
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return dateString;
      const hasTime = dateString.includes('T') || dateString.includes(':');
      return format(d, hasTime ? 'EEEE, MMMM d, yyyy • h:mm a' : 'EEEE, MMMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const isOrganiser = user && event && (user.user_id === event.organiser_id || user.role === 'admin' || user.is_admin);

  // Trigger reconcile fetch once both event + user are resolved and
  // the viewer is the organiser of a money-collecting event.
  useEffect(() => {
    if (isOrganiser && event?.organiser_collects_money) {
      fetchReconcileAttendees();
    }
  }, [isOrganiser, event?.organiser_collects_money, fetchReconcileAttendees]);

  // ── Add to Calendar helpers ──
  const getCalendarLinks = () => {
    if (!event) return {};
    const title = encodeURIComponent(event.name || '');
    const location = encodeURIComponent(event.location?.address || '');
    const desc = encodeURIComponent(event.description || '');
    const startDate = event.date ? new Date(event.date) : new Date();
    const endDate = event.end_time ? new Date(event.end_time) : new Date(startDate.getTime() + 3 * 60 * 60 * 1000);
    
    const pad = (n) => String(n).padStart(2, '0');
    const toGCal = (d) => `${d.getUTCFullYear()}${pad(d.getUTCMonth()+1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
    
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${toGCal(startDate)}/${toGCal(endDate)}&details=${desc}&location=${location}`;
    
    const icsContent = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
      `DTSTART:${toGCal(startDate)}`, `DTEND:${toGCal(endDate)}`,
      `SUMMARY:${event.name || ''}`, `DESCRIPTION:${event.description || ''}`,
      `LOCATION:${event.location?.address || ''}`,
      'END:VEVENT', 'END:VCALENDAR'
    ].join('\r\n');
    
    return { googleUrl, icsContent };
  };

  const handleAddToCalendar = (type) => {
    const { googleUrl, icsContent } = getCalendarLinks();
    if (type === 'google') {
      window.open(googleUrl, '_blank');
    } else {
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(event?.name || 'event').replace(/\s+/g, '_')}.ics`;
      a.click();
      URL.revokeObjectURL(url);
    }
    toast.success('Added to calendar!');
  };

  // ── Promo code validation ──
  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const res = await axios.post(`${API}/promo-codes/validate?event_id=${event.event_id}&code=${encodeURIComponent(promoCode.trim())}`);
      setPromoApplied(res.data);
      toast.success(`Code "${res.data.code}" applied!`);
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Invalid code');
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  // ── Fetch attendees + analytics ──
  const fetchAttendees = async () => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const [attendeesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/events/${event.event_id}/ticket-holders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events/${event.event_id}/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAttendeeList(attendeesRes.data);
      setAnalyticsData(analyticsRes.data);
    } catch (err) {
      toast.error('Failed to load attendee data');
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/events/${event.event_id}/ticket-holders/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(event?.name || 'event').replace(/\s+/g, '_')}_attendees.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  // Handle thumbnail upload for edit form
  const handleEditThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setThumbnailUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/gcs`, {
        file_data: base64,
        filename: file.name,
        upload_type: 'event_image'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditForm(prev => ({ ...prev, image_url: response.data.url }));
      toast.success('Thumbnail uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload thumbnail');
    } finally {
      setThumbnailUploading(false);
    }
  };

  // Handle flyer upload for edit form
  const handleEditFlyerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setFlyerUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/gcs`, {
        file_data: base64,
        filename: file.name,
        upload_type: 'event_image'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditForm(prev => ({ ...prev, flyer_url: response.data.url }));
      toast.success('Flyer uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload flyer');
    } finally {
      setFlyerUploading(false);
    }
  };

  // Thumbnail position/zoom controls
  const MOVE_STEP = 10;
  const SCALE_STEP = 0.1;

  const movePosition = (direction) => {
    setEditForm(prev => {
      const { x, y, scale } = prev.thumbnail_position;
      let newX = x, newY = y;
      switch(direction) {
        case 'up': newY = Math.max(0, y - MOVE_STEP); break;
        case 'down': newY = Math.min(100, y + MOVE_STEP); break;
        case 'left': newX = Math.max(0, x - MOVE_STEP); break;
        case 'right': newX = Math.min(100, x + MOVE_STEP); break;
        default: break;
      }
      return { ...prev, thumbnail_position: { x: newX, y: newY, scale: scale || 1 } };
    });
  };

  const adjustScale = (direction) => {
    setEditForm(prev => {
      const { x, y, scale = 1 } = prev.thumbnail_position;
      let newScale = scale;
      if (direction === 'in') newScale = Math.min(2, scale + SCALE_STEP);
      if (direction === 'out') newScale = Math.max(0.5, scale - SCALE_STEP);
      return { ...prev, thumbnail_position: { x, y, scale: newScale } };
    });
  };

  const resetPosition = () => {
    setEditForm(prev => ({ ...prev, thumbnail_position: { x: 50, y: 50, scale: 1 } }));
  };

  const handleThumbnailDrag = (e) => {
    if (!isDragging || !thumbnailPreviewRef.current) return;
    const rect = thumbnailPreviewRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setEditForm(prev => ({ ...prev, thumbnail_position: { x, y, scale: prev.thumbnail_position.scale || 1 } }));
  };

  const handleThumbnailTouchDrag = (e) => {
    if (!isDragging || !thumbnailPreviewRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = thumbnailPreviewRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((touch.clientY - rect.top) / rect.height) * 100));
    setEditForm(prev => ({ ...prev, thumbnail_position: { x, y, scale: prev.thumbnail_position.scale || 1 } }));
  };

  const handleEditEvent = async () => {
    if (!editForm.name || !editForm.date || !editForm.location.address) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!editForm.image_url) {
      toast.error('Please upload an event thumbnail');
      return;
    }
    
    if (!editForm.flyer_url) {
      toast.error('Please upload an event flyer');
      return;
    }

    // Combine date and time
    const eventDate = new Date(editForm.date);
    const [hours, minutes] = editForm.time.split(':');
    eventDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    setEditLoading(true);
    try {
      await axios.put(`${API}/events/${eventId}`, {
        name: editForm.name,
        description: editForm.description,
        date: eventDate.toISOString(),
        location: editForm.location,
        image_url: editForm.image_url,
        flyer_url: editForm.flyer_url,
        ticket_link: editForm.tickets_enabled ? null : editForm.ticket_link,
        tickets_enabled: editForm.tickets_enabled,
        ticket_price_pence: editForm.tickets_enabled
          ? (editForm.ticket_releases.length > 0
            ? Math.round(parseFloat(editForm.ticket_releases[0]?.price || editForm.ticket_price) * 100)
            : Math.round(parseFloat(editForm.ticket_price) * 100))
          : null,
        ticket_quantity: editForm.tickets_enabled
          ? (editForm.ticket_releases.length > 0
            ? editForm.ticket_releases.reduce((sum, r) => sum + (parseInt(r.quantity) || 0), 0)
            : parseInt(editForm.ticket_quantity))
          : null,
        max_per_buyer: editForm.tickets_enabled ? parseInt(editForm.max_per_buyer) : 6,
        booking_fee_percent: editForm.booking_fee_override ? parseFloat(editForm.booking_fee_percent) || null : null,
        booking_fee_fixed_pence: editForm.booking_fee_override ? parseInt(editForm.booking_fee_fixed_pence) || null : null,
        fee_passed_to_buyer: editForm.booking_fee_override ? editForm.fee_passed_to_buyer : null,
        ticket_releases: editForm.tickets_enabled && editForm.ticket_releases.length > 0
          ? editForm.ticket_releases.map(r => ({
              name: r.name,
              price_pence: Math.round(parseFloat(r.price) * 100),
              quantity: parseInt(r.quantity) || 0,
              end_date: r.end_date || null,
              sold: r.sold || 0
            }))
          : null,
        has_onsite_parking: editForm.has_onsite_parking,
        alternative_parking: editForm.alternative_parking || null,
        requires_id: editForm.requires_id,
        has_bar: editForm.has_bar,
        has_food_vendors: editForm.has_food_vendors,
        has_toilets: editForm.has_toilets,
        has_skate_hire: editForm.has_skate_hire,
        nearest_stations: editForm.nearest_stations.filter(s => s.trim()),
        thumbnail_position: editForm.thumbnail_position
      }, {  });

      toast.success('Event updated successfully!');
      setEditDialog(false);
      fetchEvent(); // Refresh event data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/events/${eventId}`, {  });
      toast.success('Event deleted successfully');
      navigate('/events');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBookTrip = async (trip) => {
    if (!user) {
      toast.error('Please login to book a ride');
      navigate('/login', { state: { from: `/events/${eventId}` } });
      return;
    }
    setSelectedTrip(trip);
    setSelectedPickup(0);
    setIsReturnTrip(false);
    setSelectedLeg('round_trip');
    setTripAvailability(null);
    setPickupConfirmed(false);
    setBookingDialog(true);
    // Fetch per-leg availability + prices for the radio choices
    try {
      const r = await axios.get(`${API}/trips/${trip.trip_id}/availability`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` }
      });
      setTripAvailability(r.data);
    } catch (e) {
      // non-fatal — booking can still proceed with default round_trip
    }
  };

  const handleShareEvent = async () => {
    const siteOrigin = window.location.origin;
    const shareUrl = `${siteOrigin}/api/share/events/${eventId}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: event.name, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Event link copied!');
        }
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Event link copied!');
    }
  };

  const handleShareTrip = async (trip) => {
    const siteOrigin = window.location.origin;
    const shareUrl = `${siteOrigin}/api/share/lift/${trip.trip_id}`;
    const shareText = `Lift offered to ${event?.name || 'an event'} — share the ride, split the fuel.`;

    if (navigator.share) {
      try {
        await navigator.share({ title: `Lift to ${event?.name || 'event'}`, text: shareText, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
          toast.success('Lift link copied!');
        }
      }
    } else {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Lift link copied!');
    }
  };

  const confirmBooking = async () => {
    if (!selectedTrip) return;

    setBookingLoading(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        trip_id: selectedTrip.trip_id,
        pickup_point_index: selectedPickup,
        is_return_trip: selectedLeg === 'round_trip',
        leg: selectedLeg,
      }, {  });

      toast.success('Booking created! Proceed to payment.');
      setBookingDialog(false);
      navigate(`/booking/${response.data.booking_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setBookingLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < Math.round(rating) ? 'fill-primary text-primary' : 'text-muted-foreground/30'}`} 
      />
    ));
  };

  // Parse pickup_area text into individual location strings
  const parsePickupLocations = (pickupArea) => {
    if (!pickupArea) return [];
    return pickupArea
      .split(/[\n•,]+/)
      .map(s => s.replace(/^[\s\-–—]+/, '').trim())
      .filter(s => s.length > 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Event not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen -mt-2 md:-mt-10">
      {/* Guest-only "log in to RSVP" nudge — only shown for unauthenticated
          visitors so we never distract logged-in users mid-flow. z-[60]
          keeps it above the fixed Navbar (z-50) so it can actually be
          seen and tapped. */}
      {!user && event && (
        <div
          className="sticky top-0 z-[60] backdrop-blur-md"
          style={{
            background: 'linear-gradient(135deg, rgba(147,51,234,0.95) 0%, rgba(219,39,119,0.95) 100%)',
          }}
          data-testid="guest-rsvp-nudge"
        >
          <div className="max-w-4xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
            <p className="text-xs sm:text-sm text-white font-medium leading-tight">
              <span className="font-bold">Log in to RSVP</span> · save this event & get updates
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/events/${eventId}`)}`)}
                className="px-3 py-1.5 bg-white text-black rounded-full text-xs font-bold hover:bg-white/90 transition-colors"
                data-testid="guest-rsvp-login-btn"
              >
                Log in
              </button>
            </div>
          </div>
        </div>
      )}
      <EventSEO event={event} />
      <Navbar />
      <PurchaseConfirmDialog
        open={purchaseConfirm.open}
        onOpenChange={(o) => setPurchaseConfirm(p => ({ ...p, open: o }))}
        eventName={purchaseConfirm.eventName}
        ticketCount={purchaseConfirm.ticketCount}
        paymentType={purchaseConfirm.paymentType}
      />
      
      {/* Hero Section — bumped from h-64/h-80 to h-80/h-96 and reduced the
          content overlap so the event flyer has noticeably more vertical
          breathing room on mobile and tablet. */}
      <div className="relative h-80 md:h-96 overflow-hidden">
        <img loading="lazy" 
          src={event.image_url || "https://images.unsplash.com/photo-1765574782625-198093f8c9ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzY2VuaWMlMjBoaWdod2F5JTIwcm9hZCUyMHRyaXAlMjBhc3BoYWx0fGVufDB8fHx8MTc3MDU1ODk1MHww&ixlib=rb-4.1.0&q=60&w=800"}
          alt={event.name}
          className="w-full h-full object-cover transition-transform"
          style={event.thumbnail_position ? { 
            objectPosition: `${event.thumbnail_position.x}% ${event.thumbnail_position.y}%`,
            transform: `scale(${event.thumbnail_position.scale || 1})`
          } : undefined}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
      </div>

      {/* Content */}
      <main className="px-4 md:px-8 pb-12 -mt-12 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Event Info */}
          <Card className="bg-card border-border/50 mb-8">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div className="space-y-4">
                  <div>
                    <h1 className="font-heading text-3xl md:text-4xl break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{event.name}</h1>
                    <div className="flex gap-1.5 mt-3">
                      {!isVenue && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="rounded-full" data-testid="add-to-calendar-btn">
                            <CalendarIcon className="w-4 h-4 mr-1.5" /> Calendar
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-2" align="start">
                          <button onClick={() => handleAddToCalendar('google')} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors" data-testid="add-google-cal">
                            Google Calendar
                          </button>
                          <button onClick={() => handleAddToCalendar('ics')} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-accent transition-colors" data-testid="add-apple-cal">
                            Apple / Outlook (.ics)
                          </button>
                        </PopoverContent>
                      </Popover>
                      )}
                      <Button variant="outline" size="sm" className="rounded-full" onClick={handleShareEvent} data-testid="share-event-btn">
                        <Share2 className="w-4 h-4 mr-1.5" /> Share
                      </Button>
                    </div>
                    {event.host_username && (
                      <div className="mt-2">
                        <a
                          href={`https://www.instagram.com/${String(event.host_username).replace(/^@/, '')}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-primary/40 bg-primary/10 hover:bg-primary/20 hover:border-primary transition text-sm font-medium text-primary max-w-full"
                          data-testid="event-host-handle-link"
                          title="View the organiser on Instagram"
                        >
                          <Instagram className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">@{String(event.host_username).replace(/^@/, '')}</span>
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {event.is_recurring && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/30 text-xs text-primary font-medium">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                      Recurring {event.recurrence_pattern === 'weekly' ? '(Weekly)' : event.recurrence_pattern === 'biweekly' ? '(Fortnightly)' : event.recurrence_pattern === 'monthly' ? '(Monthly)' : ''}
                    </div>
                  )}

                  {event.status === 'cancelled' && (
                    <div className="w-full rounded-xl bg-destructive/15 border border-destructive/40 p-3">
                      <p className="text-destructive font-bold text-sm">This event has been cancelled</p>
                      <p className="text-destructive/70 text-xs mt-0.5">All ticket holders have been notified and refunds have been processed.</p>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-5 h-5 text-primary" />
                      <span>
                        {isVenue ? (
                          event.opening_hours || 'Open daily'
                        ) : (
                          <>
                            {formatDate(event.date)}
                            {event.end_time && (
                              <span className="text-muted-foreground/70"> - {
                                event.end_time.includes('T')
                                  ? new Date(event.end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })
                                  : event.end_time
                              }</span>
                            )}
                          </>
                        )}
                      </span>
                    </div>
                    {event.additional_dates?.length > 0 && (
                      <div className="flex items-start gap-2 text-muted-foreground ml-7">
                        <span className="text-xs">Also on: {event.additional_dates.map(d => formatDate(d)).join(' | ')}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-5 h-5 text-secondary" />
                      <span>{event.location?.address}</span>
                    </div>
                  </div>

                  {event.description && (
                    <p className="text-muted-foreground max-w-2xl">{event.description}</p>
                  )}

                  {/* Venue Info Badges */}
                  <div className="flex flex-wrap gap-2">
                    {event.requires_id && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-magenta-500/10 border border-[#d946ef]/30 text-xs text-[#d946ef] font-medium">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>
                        ID Required
                      </span>
                    )}
                    {event.has_bar && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 text-xs text-[#8b5cf6] font-medium">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                        Licensed Bar
                      </span>
                    )}
                    {event.has_food_vendors && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#22d3ee]/10 border border-[#22d3ee]/30 text-xs text-[#22d3ee] font-medium">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" /></svg>
                        Food Available
                      </span>
                    )}
                    {event.has_toilets && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#22d3ee]/10 border border-[#22d3ee]/30 text-xs text-[#22d3ee] font-medium">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" /></svg>
                        Toilets
                      </span>
                    )}
                    {event.has_skate_hire && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#d946ef]/10 border border-[#d946ef]/30 text-xs text-[#d946ef] font-medium">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        Skate Hire
                      </span>
                    )}
                  </div>

                  {/* Nearest Stations */}
                  {event.nearest_stations?.length > 0 && (
                    <div className="flex items-start gap-2 mt-2">
                      <svg className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M8 11h8m-4 4v4m-4 0h8M5 3h14a1 1 0 011 1v12a3 3 0 01-3 3H7a3 3 0 01-3-3V4a1 1 0 011-1z" /></svg>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">Nearest station{event.nearest_stations.length > 1 ? 's' : ''}:</span>{' '}
                        {event.nearest_stations.join(', ')}
                      </div>
                    </div>
                  )}

                  {/* Skate at own risk notice */}
                  <p className="text-[10px] text-muted-foreground/50 mt-3 italic">All riders take part at their own risk. See <a href="/terms" className="underline hover:text-muted-foreground">Terms</a>.</p>

                  {/* Parking Information */}
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <ParkingCircle className="w-5 h-5 text-accent" />
                    {event.has_onsite_parking ? (
                      <span className="text-success">Onsite parking available</span>
                    ) : (
                      <span>
                        No onsite parking
                        {event.alternative_parking && (
                          <span className="text-muted-foreground/70"> • {event.alternative_parking}</span>
                        )}
                      </span>
                    )}
                  </div>

                  {event.organiser_name && event.organiser_name.toLowerCase() !== 'admin' && event.organiser_name !== 'RollM8s Team' && (
                    <p className="text-sm text-muted-foreground">
                      Uploaded by <span className="text-foreground">{event.organiser_name}</span>
                    </p>
                  )}
                  
                  {/* Ticket Buttons */}
                  {event.tickets_enabled ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => {
                            if (!user) {
                              toast.error('Please login to get tickets');
                              navigate('/login', { state: { from: `/events/${eventId}` } });
                              return;
                            }
                            setTicketDialog(true);
                            // Fetch fee config
                            axios.get(`${API}/ticket-fee-config/${eventId}`).then(res => setFeeConfig(res.data)).catch(() => {});
                          }}
                          className="rounded-full bg-accent hover:bg-accent/90 flex-1"
                          disabled={(event.ticket_quantity || 0) <= (event.tickets_sold || 0)}
                          data-testid="buy-tickets-btn"
                        >
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M22 10V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v4c1.1 0 2 .9 2 2s-.9 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2s.9-2 2-2zm-2-1.46c-1.19.69-2 1.99-2 3.46s.81 2.77 2 3.46V18H4v-2.54c1.19-.69 2-1.99 2-3.46 0-1.48-.81-2.77-2-3.46V6h16v2.54z"/></svg>
                          {(event.ticket_quantity || 0) <= (event.tickets_sold || 0) ? 'Sold Out' : (() => {
                            const rel = feeConfig?.active_release;
                            const price = rel ? rel.price_pence : event.ticket_price_pence;
                            if (!price || price === 0) return 'Get Free Ticket';
                            const currency = event.currency || 'GBP';
                            const fmt = formatCurrency(price, { currency, minor: true });
                            return rel ? `${rel.name} — ${fmt}` : `Buy Ticket — ${fmt}`;
                          })()}
                        </Button>
                        {isOrganiser && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/events/${eventId}/check-in`)}
                            className="rounded-full border-success/30 text-success hover:bg-success/10 flex-shrink-0"
                            data-testid="check-in-btn"
                          >
                            <ScanLine className="w-4 h-4 mr-1" />
                            Check-In
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {Math.max(0, (event.ticket_quantity || 0) - (event.tickets_sold || 0))} of {event.ticket_quantity} remaining
                        {feeConfig?.active_release && ` (${feeConfig.active_release.name})`}
                      </p>
                      {/* Waitlist for sold-out events */}
                      {(event.ticket_quantity || 0) <= (event.tickets_sold || 0) && user && (
                        <div className="mt-2">
                          {waitlistStatus?.on_waitlist ? (
                            <div className="space-y-1.5">
                              <p className="text-xs text-primary">You're #{waitlistStatus.position} on the waitlist ({waitlistStatus.total_waiting} waiting)</p>
                              <Button variant="outline" size="sm" className="rounded-full text-xs"
                                disabled={waitlistLoading}
                                onClick={async () => {
                                  setWaitlistLoading(true);
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.delete(`${API}/events/${eventId}/waitlist/leave`, { headers: { Authorization: `Bearer ${token}` } });
                                    setWaitlistStatus({ on_waitlist: false, total_waiting: Math.max(0, (waitlistStatus?.total_waiting || 1) - 1) });
                                    toast.success('Left the waitlist');
                                  } catch { toast.error('Failed to leave waitlist'); }
                                  finally { setWaitlistLoading(false); }
                                }}
                                data-testid="leave-waitlist-btn">
                                {waitlistLoading ? 'Processing...' : 'Leave Waitlist'}
                              </Button>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm" className="rounded-full text-xs"
                              disabled={waitlistLoading}
                              onClick={async () => {
                                setWaitlistLoading(true);
                                try {
                                  const token = localStorage.getItem('token');
                                  const res = await axios.post(`${API}/events/${eventId}/waitlist/join`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                  setWaitlistStatus({ on_waitlist: true, position: res.data.position, total_waiting: (waitlistStatus?.total_waiting || 0) + 1 });
                                  toast.success(`You're #${res.data.position} on the waitlist!`);
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join waitlist'); }
                                finally { setWaitlistLoading(false); }
                              }}
                              data-testid="join-waitlist-btn">
                              {waitlistLoading ? 'Processing...' : `Join Waitlist${waitlistStatus?.total_waiting ? ` (${waitlistStatus.total_waiting} waiting)` : ''}`}
                            </Button>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-1">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setShowRefundInfo(!showRefundInfo); }}
                          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/70 hover:text-primary transition-colors"
                          data-testid="refund-info-btn"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2}/><path strokeLinecap="round" strokeWidth={2} d="M12 16v-4m0-4h.01" /></svg>
                          Refund policy
                        </button>
                      </div>
                      {showRefundInfo && (
                        <div className="mt-2 p-2.5 rounded-lg bg-muted/30 border border-border/50 text-[11px] text-muted-foreground space-y-1">
                          <p className="font-medium text-foreground text-xs">Ticket Refund Policy</p>
                          <p>Full refund if the organiser cancels the event.</p>
                          <p>Refund requests up to 48hrs before the event — contact the organiser directly.</p>
                          <p>No refunds within 48hrs of the event start time.</p>
                          <a href="/refund-policy" target="_blank" className="text-primary hover:underline inline-flex items-center gap-0.5">
                            Read full refund policy <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          </a>
                        </div>
                      )}
                    </div>
                  ) : event.ticket_link && (
                    <Button
                      onClick={() => window.open(event.ticket_link, '_blank')}
                      className="rounded-full bg-accent hover:bg-accent/90"
                      data-testid="buy-tickets-ext-btn"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M22 10V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v4c1.1 0 2 .9 2 2s-.9 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2s.9-2 2-2zm-2-1.46c-1.19.69-2 1.99-2 3.46s.81 2.77 2 3.46V18H4v-2.54c1.19-.69 2-1.99 2-3.46 0-1.48-.81-2.77-2-3.46V6h16v2.54z"/></svg>
                      Buy Tickets
                    </Button>
                  )}
                </div>

                <div className="flex flex-col items-start gap-3">
                  {/* Drivers Available */}
                  {event.trips?.length > 0 && (
                    <div className="w-full p-3 rounded-xl border border-success/20 bg-success/5 mb-2" data-testid="drivers-available-badge">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-success/15 flex items-center justify-center">
                          <Car className="w-5 h-5 text-success" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-success">{event.trips.length} driver{event.trips.length > 1 ? 's' : ''} available</p>
                          <p className="text-[11px] text-muted-foreground">Offering rides to this event</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Group Chat Button */}
                  {!isEventEnded && (
                    <Button
                      onClick={handleOpenChat}
                      variant="outline"
                      className="rounded-full border-primary/30 text-primary hover:bg-primary/10 gap-2"
                      data-testid="event-group-chat-btn"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Group Chat
                    </Button>
                  )}
                  
                  {/* Organiser buttons */}
                  {isOrganiser && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        {event.tickets_enabled && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setAttendeeDialog(true); fetchAttendees(); }}
                            className="rounded-full border-secondary/30 text-secondary hover:bg-secondary/10 flex-1"
                            data-testid="attendee-list-btn"
                          >
                            <Users className="w-4 h-4 mr-1" />
                            Attendees
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              await axios.post(`${API}/events/${eventId}/archive`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Event archived');
                              navigate('/dashboard');
                            } catch { toast.error('Failed to archive'); }
                          }}
                          className="rounded-full border-muted-foreground/30 text-muted-foreground hover:bg-muted/20 flex-1"
                          data-testid="archive-event-btn"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                          Archive
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditDialog(true)}
                          className="rounded-full border-primary/30 text-primary hover:bg-primary/10"
                          data-testid="edit-event-btn"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        {(!event.tickets_sold || event.tickets_sold === 0) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteDialog(true)}
                            className="rounded-full border-destructive/30 text-destructive hover:bg-destructive/10"
                            data-testid="delete-event-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        ) : <div />}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (!window.confirm(`Cancel "${event.name}"?\n\nAll ticket holders will be automatically refunded. This cannot be undone.`)) return;
                            try {
                              const token = localStorage.getItem('token');
                              const { data } = await axios.post(`${API}/events/${eventId}/cancel`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success(`Event cancelled. ${data.tickets_refunded || 0} payment(s) refunded.`);
                              fetchEvent();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel'); }
                          }}
                          className="rounded-full border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                          data-testid="cancel-event-btn"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Stripe Connect status for organiser */}
                  {isOrganiser && event.tickets_enabled && organiserStripeStatus && organiserStripeStatus.status !== 'complete' && (
                    <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 mt-3 space-y-3" data-testid="organiser-stripe-banner">
                      <p className="text-sm font-semibold text-primary">Get paid directly to your bank</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        We use Stripe — the same payment system behind Uber, Deliveroo and Amazon.
                        Setup takes about <span className="text-foreground font-medium">2 minutes</span>. You'll just need:
                      </p>
                      <div className="space-y-1.5 text-xs text-muted-foreground pl-1">
                        <span className="flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                          Your name &amp; date of birth
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                          Bank details (sort code &amp; account number)
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                          A photo of your ID (passport or driving licence)
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground/70 leading-relaxed">
                        Once connected, ticket revenue is automatically paid out to your bank 48 hours after each event. No chasing payments, no invoices.
                      </p>
                      <Button
                        size="sm"
                        onClick={() => setShowOrganiserStripeConfirm(true)}
                        disabled={stripeConnectLoading}
                        className="rounded-full w-full gap-2"
                        data-testid="organiser-stripe-connect-btn"
                      >
                        {stripeConnectLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                        {organiserStripeStatus?.status === 'pending' ? 'Continue Stripe Setup' : 'Set Up in 2 Minutes'}
                      </Button>
                    </div>
                  )}
                  {isOrganiser && event.tickets_enabled && organiserStripeStatus?.status === 'complete' && (
                    <div className="flex items-center gap-2 text-xs text-green-400 mt-3">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>Stripe connected — ticket revenue goes to your account</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Promo Codes Management - Organiser Only */}
              {isOrganiser && event.tickets_enabled && (
                <PromoCodesSection eventId={event.event_id} />
              )}

              {/* Embed Widget for Organisers */}
              {isOrganiser && event.tickets_enabled && (
                <div className="mt-4 p-4 rounded-xl border border-border/50 bg-muted/10" data-testid="embed-widget-section">
                  <p className="text-sm font-semibold mb-2">Embed Ticket Widget</p>
                  <p className="text-xs text-muted-foreground mb-3">Paste this code on your website or Linktree to sell tickets directly:</p>
                  <div className="relative">
                    <code className="block p-3 rounded-lg bg-background/80 border border-border/50 text-[10px] text-muted-foreground break-all select-all leading-relaxed"
                      data-testid="embed-code">
                      {`<iframe src="${window.location.origin}/api/widget/embed/${event.event_id}" width="100%" height="600" frameborder="0" style="border:none;border-radius:16px;max-width:420px;"></iframe>`}
                    </code>
                    <Button size="sm" variant="outline" className="absolute top-2 right-2 rounded text-xs h-7 px-2"
                      onClick={() => {
                        navigator.clipboard.writeText(`<iframe src="${window.location.origin}/api/widget/embed/${event.event_id}" width="100%" height="600" frameborder="0" style="border:none;border-radius:16px;max-width:420px;"></iframe>`);
                        toast.success('Embed code copied!');
                      }}
                      data-testid="copy-embed-btn">
                      Copy
                    </Button>
                  </div>
                </div>
              )}

              {/* Who's Going? */}
              <div className="mt-6 pt-6 border-t border-border/50" data-testid="whos-going-section">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-base font-semibold text-foreground">Who's going</p>
                  {user && (
                    <Button
                      size="sm"
                      variant={imGoing ? "default" : "outline"}
                      className={`rounded-full text-sm px-4 ${imGoing ? 'bg-primary hover:bg-primary/90' : 'border-primary/40 text-primary hover:bg-primary/10'}`}
                      onClick={async () => {
                        if (!user) { toast.error('Log in to mark yourself as going'); return; }
                        setGoingLoading(true);
                        const wasGoing = imGoing;
                        const prevAttendees = attendees;
                        // Optimistic update
                        setImGoing(!wasGoing);
                        if (wasGoing) {
                          setAttendees(prev => prev.filter(a => a.user_id !== user.user_id));
                        } else {
                          setAttendees(prev => [...prev, { user_id: user.user_id, name: user.name || '', username: user.username, photo: user.selfie_url || user.picture, role: 'going' }]);
                        }
                        try {
                          const token = localStorage.getItem('token');
                          const res = await axios.post(`${API}/events/${eventId}/going`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          setImGoing(res.data.going);
                          // Refresh attendees from server for accuracy
                          const attRes = await axios.get(`${API}/events/${eventId}/attendees`);
                          setAttendees(attRes.data?.attendees || []);
                          toast.success(res.data.going ? "You're going!" : "Removed from going");
                        } catch {
                          // Revert on error
                          setImGoing(wasGoing);
                          setAttendees(prevAttendees);
                          toast.error('Something went wrong');
                        }
                        finally { setGoingLoading(false); }
                      }}
                      disabled={goingLoading}
                      data-testid="im-going-btn"
                    >
                      {goingLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                      ) : imGoing ? (
                        <CheckCircle className="w-4 h-4 mr-1.5" />
                      ) : (
                        <UserPlus className="w-4 h-4 mr-1.5" />
                      )}
                      {imGoing ? "I'm Going" : "I'm Going"}
                    </Button>
                  )}
                </div>
                {attendees.length > 0 ? (
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-3">
                      {attendees.slice(0, 10).map((a, i) => (
                        <Link key={a.user_id} to={`/profile/${a.user_id}`} className="relative block" style={{ zIndex: 10 - i }} data-testid={`attendee-avatar-${a.user_id}`}>
                        <Avatar
                          className="h-10 w-10 ring-2 ring-background cursor-pointer hover:ring-primary transition-all"
                          title={handle(a)}
                        >
                          <AvatarImage src={a.photo} />
                          <AvatarFallback className="text-xs bg-primary/20 text-primary font-bold">
                            {(a.username || a.name || '?')[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        {a.community_verified && (
                          <div className="absolute -bottom-0.5 -right-0.5 bg-background rounded-full p-px">
                            <VerifiedBadge size={12} />
                          </div>
                        )}
                        </Link>
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground ml-1">
                      {attendees.length === 1
                        ? `1 person going`
                        : `${attendees.length} going`
                      }
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No one yet — be the first!</p>
                )}
              </div>
              
              {/* Civilian Nights — Pay & Reconcile. Hidden entirely when
                  the event isn't collecting money. Locked behind RSVP
                  for non-organisers (the component renders a locked
                  state until the viewer taps I'm Going). */}
              {event.organiser_collects_money && (
                <div className="mt-6 pt-6 border-t border-border/50">
                  <EventPayReconcilePanel
                    event={event}
                    imGoing={imGoing}
                    currentUser={user}
                    onPaymentChanged={() => {
                      if (isOrganiser) fetchReconcileAttendees();
                    }}
                  />
                </div>
              )}

              {/* Organiser-only reconcile panel — list of going attendees
                  with mark-received / refund buttons. */}
              {isOrganiser && event.organiser_collects_money && (
                <div className="mt-6">
                  <EventPayReconcileOrganiserPanel
                    event={event}
                    attendees={reconcileAttendees}
                    onRefresh={fetchReconcileAttendees}
                  />
                </div>
              )}

              {/* Offer a Ride CTA - above flyer */}
              <div className="mt-6 pt-6 border-t border-border/50">
                <Card className="bg-gradient-to-r from-secondary/20 to-primary/20 border-secondary/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary/30 flex items-center justify-center">
                          <Car className="w-5 h-5 text-secondary" />
                        </div>
                        <div>
                          <p className="font-medium">Heading to this event?</p>
                          <p className="text-sm text-muted-foreground">Offer seats and share petrol costs</p>
                        </div>
                      </div>
                      <Button 
                        onClick={() => user ? navigate('/create-trip', { state: { eventId: event.event_id, eventName: event.name } }) : navigate('/register')}
                        className="rounded-full bg-secondary hover:bg-secondary/90"
                        data-testid="offer-ride-cta-btn"
                      >
                        Offer a Ride
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Event Flyer */}
              {event.flyer_url && (
                <div className="mt-6 pt-6 border-t border-border/50">
                  <p className="text-sm font-medium text-muted-foreground mb-3">Event Flyer</p>
                  <div className="flex justify-center">
                    <div className="relative inline-block">
                      <a href={event.flyer_url} target="_blank" rel="noopener noreferrer">
                        <img loading="lazy" 
                          src={event.flyer_url} 
                          alt={`${event.name} flyer`}
                          className={`max-w-full max-h-96 rounded-xl object-contain border border-border/50 hover:opacity-90 transition-opacity cursor-pointer ${event.status === 'cancelled' ? 'opacity-40' : ''}`}
                        />
                      </a>
                      {event.status === 'cancelled' && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="bg-destructive/90 px-6 py-3 rounded-xl -rotate-12 shadow-lg">
                            <span className="text-white font-black text-3xl tracking-widest uppercase">CANCELLED</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
            </CardContent>
          </Card>

          {/* Weather Widget - Above the "Heading to this event?" section */}
          {event.location?.lat && event.location?.lng && (
            <div className="mb-6">
              <p className="text-sm font-medium text-muted-foreground mb-3">Weather at Venue</p>
              <WeatherWidget 
                latitude={event.location.lat}
                longitude={event.location.lng}
                eventDate={event.date}
                eventName={event.name}
              />
            </div>
          )}

          {/* Drivers Section */}
          <div className="space-y-6">

            {/* Available Drivers Header - directly above driver cards */}
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl">
                {event.trips && event.trips.length > 0 ? (
                  <>Available <span className="gradient-text">Drivers</span></>
                ) : (
                  <>No <span className="gradient-text">Drivers</span> Available Yet</>
                )}
              </h2>
            </div>

            {/* "Let others know..." CTA — shown when the current user is the driver of at least one lift on this event */}
            {user && event.trips?.some(t => t.driver_id === user.user_id) && (
              <Button
                onClick={() => {
                  const myTrip = event.trips.find(t => t.driver_id === user.user_id);
                  if (myTrip) handleShareTrip(myTrip);
                }}
                className="w-full h-auto min-h-12 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-semibold flex items-center justify-center gap-2 px-4 py-3 whitespace-normal leading-tight"
                data-testid="event-share-my-lift-cta"
              >
                <Share2 className="w-4 h-4 flex-shrink-0" />
                <span>Let others know there&apos;s a lift available</span>
              </Button>
            )}

            {event.trips && event.trips.length > 0 && (
              <div className="grid gap-4">
                {event.trips.map((trip) => (
                  <Card 
                    key={trip.trip_id} 
                    className="border-2 border-primary/50 hover:border-primary/70 transition-colors bg-card"
                    style={{ borderRadius: '0px' }}
                    data-testid={`trip-card-${trip.trip_id}`}
                  >
                    <CardContent className="p-5">
                      {/* Driver Details Label + Round-trip distance pill */}
                      <div className="flex items-center justify-between mb-3 gap-2">
                        <p className="font-heading text-xl text-foreground">Driver <span className="gradient-text">Details</span></p>
                        {trip.total_distance_miles && (
                          <div
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex-shrink-0"
                            data-testid={`trip-distance-${trip.trip_id}`}
                            title="Round-trip distance — pickup → event → return"
                          >
                            <Navigation className="w-3.5 h-3.5 text-cyan-400" />
                            <span className="text-xs font-semibold text-cyan-400 leading-none">
                              {trip.total_distance_miles} mi
                            </span>
                            <span className="text-[10px] text-muted-foreground leading-none hidden sm:inline">round trip</span>
                          </div>
                        )}
                      </div>
                      {/* Driver Info */}
                      <div className="flex items-start gap-3">
                        <Avatar 
                          className={`w-12 h-12 border-2 border-primary flex-shrink-0 ${trip.driver_is_admin ? 'cursor-default' : 'cursor-pointer hover:border-primary/70'} transition-colors`}
                          onClick={() => !trip.driver_is_admin && navigate(`/profile/${trip.driver_id}`)}
                        >
                          <AvatarImage src={trip.driver_photo || trip.driver_selfie_url || trip.driver_picture} alt={handle(trip, 'driver')} />
                          <AvatarFallback className="bg-primary/20 text-primary font-heading text-lg">
                            {(trip.driver_username || trip.driver_name || 'D')[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 
                              className={`font-semibold text-base text-foreground ${trip.driver_is_admin ? 'cursor-default' : 'cursor-pointer hover:text-primary'} transition-colors`}
                              onClick={() => !trip.driver_is_admin && navigate(`/profile/${trip.driver_id}`)}
                            >
                              {handle(trip, 'driver')}
                            </h3>
                            <VerifiedBadge show={trip.driver_verified} size={16} />
                            {trip.driver_experience_level === 'ambassador' && (
                              <Badge variant="outline" className="border-primary/30 text-primary text-xs">Ambassador</Badge>
                            )}
                            {trip.driver_experience_level === 'experienced' && (
                              <Badge variant="outline" className="border-primary/30 text-primary text-xs">Experienced</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-sm text-muted-foreground">
                              {trip.driver_rating > 0 ? '(New)' : '(New)'}
                            </span>
                            {(trip.driver_total_trips || 0) >= 5 && (
                              <span className="text-sm text-muted-foreground">
                                • {trip.driver_total_trips} trips
                              </span>
                            )}
                          </div>
                          {/* Trust Badges - flexible wrap */}
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {trip.driver_phone_verified && (
                              <Badge variant="outline" className="border-primary/30 text-primary/80 text-xs py-0">
                                <Phone className="w-3 h-3 mr-1" /> Verified
                              </Badge>
                            )}
                            {trip.driver_email_verified && (
                              <Badge variant="outline" className="border-primary/30 text-primary/80 text-xs py-0">
                                <Mail className="w-3 h-3 mr-1" /> Verified
                              </Badge>
                            )}
                            {trip.driver_id_verified && (
                              <Badge variant="outline" className="border-primary/30 text-primary/80 text-xs py-0">
                                <BadgeCheck className="w-3 h-3 mr-1" /> ID
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* --- Separator Line --- */}
                      <div className="border-t-2 border-primary/30 mt-4 mb-5" />

                      {/* Trip Info Section */}
                      <div className="space-y-4">
                        {/* Pickup Points */}
                        {trip.pickup_points?.length > 0 && (
                          <div className="space-y-2">
                            {trip.pickup_points.map((point, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm">
                                <MapPin className="w-4 h-4 text-secondary flex-shrink-0" />
                                <span className="font-medium text-foreground">{point.address?.split(',')[0] || 'Pickup point'}</span>
                                <span className="text-muted-foreground">• {point.time_type === 'after' ? `After ${point.time || ''}` : (point.time || '')}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Price per seat — handle "Free" (0) explicitly because
                            && treats 0 as falsy and would hide the tag entirely. */}
                        {trip.contribution_amount !== null && trip.contribution_amount !== undefined && (
                          Number(trip.contribution_amount) === 0 ? (
                            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40" data-testid={`trip-price-${trip.trip_id}`}>
                              <span className="text-emerald-400 font-bold text-lg">Free</span>
                              <span className="text-sm text-emerald-400/80">— driver covers it 🙌</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-green-500/10 border border-green-500/30" data-testid={`trip-price-${trip.trip_id}`}>
                              <span className="text-green-500 font-bold text-lg">£{Number(trip.contribution_amount).toFixed(2)}</span>
                              <span className="text-sm text-muted-foreground">per seat</span>
                            </div>
                          )
                        )}

                        {/* Share & Book Buttons */}
                        <div className="flex items-center gap-3 pt-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-full h-10 w-10 flex-shrink-0 border-primary/30 text-muted-foreground hover:text-foreground hover:border-primary/50"
                            onClick={(e) => { e.stopPropagation(); handleShareTrip(trip); }}
                            data-testid={`share-trip-${trip.trip_id}`}
                            title="Share this lift"
                          >
                            <Share2 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleBookTrip(trip)}
                            className="rounded-full bg-primary hover:bg-primary/90 flex-1"
                            disabled={trip.available_seats <= (trip.booked_seats || 0)}
                            data-testid={`book-trip-${trip.trip_id}`}
                          >
                            Book Ride • {trip.available_seats - (trip.booked_seats || 0)} seat{(trip.available_seats - (trip.booked_seats || 0)) !== 1 ? 's' : ''} left
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>

                      {/* Passengers list - only visible to the driver */}
                      {user && trip.driver_id === user.user_id && (
                        <div className="mt-4 pt-4 border-t border-primary/20">
                          {/* Event info bar */}
                          <div className="flex items-center justify-between gap-3 mb-4 p-3 rounded-lg bg-primary/5 border-2 border-primary/20">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{event.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {event.date ? new Date(event.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                              </p>
                            </div>
                            {(event.image_url || event.thumbnail_url || event.flyer_url) && (
                              <img loading="lazy" 
                                src={event.image_url || event.thumbnail_url || event.flyer_url} 
                                alt={event.name}
                                className="w-12 h-12 rounded-lg object-cover border border-primary/20 flex-shrink-0"
                              />
                            )}
                          </div>

                          <div className="flex items-center justify-between mb-3">
                            <p className="font-semibold text-sm text-foreground">Your <span className="gradient-text">Passengers</span></p>
                            <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                              {tripPassengers[trip.trip_id]?.length || 0} booked
                            </Badge>
                          </div>
                          {(!tripPassengers[trip.trip_id] || tripPassengers[trip.trip_id].length === 0) ? (
                            <p className="text-sm text-muted-foreground text-center py-3">No passengers yet</p>
                          ) : (
                            <div className="space-y-3">
                              {tripPassengers[trip.trip_id].map((passenger) => (
                                <div 
                                  key={passenger.booking_id}
                                  className="flex items-center justify-between p-4 rounded-xl bg-primary/5 border-2 border-primary/20 cursor-pointer hover:border-primary/40 transition-colors"
                                  onClick={() => navigate(`/profile/${passenger.rider_id}`)}
                                  data-testid={`passenger-${passenger.booking_id}`}
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <Avatar className="w-10 h-10 border-2 border-primary/30">
                                      {passenger.rider_profile_image ? (
                                        <AvatarImage src={passenger.rider_profile_image} alt={handle(passenger, 'rider')} />
                                      ) : null}
                                      <AvatarFallback className="bg-primary/20 text-primary text-sm">
                                        {(passenger.rider_username || passenger.rider_name || '?')[0]?.toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0">
                                      <p className="font-medium text-sm text-foreground">{handle(passenger, 'rider')}</p>
                                      <p className="text-xs text-muted-foreground">
                                        <MapPin className="w-3 h-3 inline mr-1" />
                                        {passenger.pickup_point?.address?.split(',')[0] || 'N/A'}
                                        {passenger.is_return_trip && ' • Return'}
                                      </p>
                                    </div>
                                  </div>
                                  <Badge 
                                    variant="outline" 
                                    className={`text-xs flex-shrink-0 ${
                                      passenger.payment_status === 'paid' ? 'border-success/50 text-success' :
                                      passenger.payment_status === 'pending' ? 'border-yellow-500/50 text-yellow-500' :
                                      passenger.status === 'confirmed' ? 'border-primary/50 text-primary' :
                                      'border-yellow-500/50 text-yellow-500'
                                    }`}
                                  >
                                    {passenger.payment_status === 'paid' ? 'Paid' :
                                     passenger.payment_status === 'pending' ? 'Awaiting Payment' :
                                     passenger.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

          </div>

          {/* Ride Requests Section */}
          <div className="space-y-6 mt-12" id="ride-request-section">
            {/* Ride Requests Header */}
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl">
                Ride <span className="gradient-text">Requests</span>
              </h2>
              {rideRequests.filter(r => r.status === 'waiting').length > 0 && (
                <Badge className="bg-accent/20 text-accent border-accent/30">
                  <Users className="w-4 h-4 mr-1" />
                  {rideRequests.filter(r => r.status === 'waiting').length} waiting
                </Badge>
              )}
            </div>

            {/* Ride request cards list - visible to drivers and admins who can act on them */}
            {user && (user.is_admin || user.role === 'admin' || event.trips?.some(t => t.driver_id === user.user_id)) && rideRequests.filter(r => r.status === 'waiting').length > 0 && (
              <div className="space-y-3">
                {rideRequests.filter(r => r.status === 'waiting').map((request) => {
                  const isDriver = event.trips?.some(t => t.driver_id === user.user_id);
                  const isAdmin = user.is_admin || user.role === 'admin';
                  const canInteract = isDriver || isAdmin;
                  return (
                    <Card
                      key={request.request_id}
                      className="bg-card border-2 border-primary/30 hover:border-primary/60 cursor-pointer transition-colors"
                      onClick={() => { setSelectedRequest(request); setSelectedPickupForAccept(''); setCheckedPickupsForTrip([]); setAcceptDialog(true); }}
                      data-testid={`ride-request-${request.request_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1 min-w-0">
                            <p className="font-medium">{handle(request, 'rider')}</p>
                            <div className="space-y-1">
                              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span className="text-xs font-medium">Pickup locations:</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5 pl-6">
                                {parsePickupLocations(request.pickup_area).map((loc, i) => (
                                  <Badge key={i} variant="outline" className="bg-background/50 text-xs">
                                    {loc}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Users className="w-4 h-4 flex-shrink-0" />
                              <span>{request.passenger_count} seat{request.passenger_count > 1 ? 's' : ''} needed</span>
                            </div>
                            {(request.preferred_date || request.is_venue_request) && request.preferred_date && (
                              <div className="flex items-center gap-2 text-sm text-secondary" data-testid={`request-when-${request.request_id}`}>
                                <Calendar className="w-4 h-4 flex-shrink-0" />
                                <span className="font-medium">
                                  {request.preferred_date}{request.preferred_time ? ` · ${request.preferred_time}` : ''}
                                </span>
                              </div>
                            )}
                            {request.notes && (
                              <p className="text-sm text-muted-foreground italic mt-2">"{request.notes}"</p>
                            )}
                          </div>
                          {canInteract ? (
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRequest(request);
                                setSelectedPickupForAccept('');
                                setCheckedPickupsForTrip([]);
                                setAcceptDialog(true);
                              }}
                              className="rounded-full bg-success hover:bg-success/90 flex-shrink-0"
                              data-testid={`accept-request-${request.request_id}`}
                            >
                              {isDriver ? 'Offer Lift' : 'Accept'}
                            </Button>
                          ) : (
                            <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-1" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Show accepted requests for transparency */}
            {rideRequests.filter(r => r.status === 'accepted').length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Recently matched:</p>
                {rideRequests.filter(r => r.status === 'accepted').slice(0, 3).map((request) => (
                  <div 
                    key={request.request_id}
                    className="p-3 rounded-lg bg-success/10 border border-success/20 flex items-center gap-3"
                  >
                    <BadgeCheck className="w-5 h-5 text-success" />
                    <span className="text-sm">
                      <span className="font-medium">{handle(request, 'rider')}</span>
                      {' '}got a ride from{' '}
                      <span className="font-medium">{handleWithFallback(request.accepted_by_driver_username, request.accepted_by_driver_name)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Request a Ride card - explanation + action, shown below the list */}
            <Card className="bg-card border-2 border-primary/30">
              <CardContent className="p-6">
                  {user && myRideRequest && myRideRequest.status !== 'cancelled' ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            myRideRequest.status === 'waiting' ? 'bg-accent animate-pulse' :
                            myRideRequest.status === 'accepted' ? 'bg-success' : 'bg-muted'
                          }`} />
                          <span className="font-medium">Your Ride Request</span>
                        </div>
                        <Badge variant="outline" className={
                          myRideRequest.status === 'waiting' ? 'border-accent text-accent' :
                          myRideRequest.status === 'accepted' ? 'border-success text-success' : ''
                        }>
                          {myRideRequest.status === 'waiting' ? 'Waiting for driver' :
                           myRideRequest.status === 'accepted' ? 'Accepted!' : myRideRequest.status}
                        </Badge>
                      </div>
                      
                      <div className="p-4 rounded-lg bg-background/50 space-y-2">
                        <div className="space-y-1">
                          <span className="text-sm text-muted-foreground">Pickup Locations:</span>
                          <div className="flex flex-wrap gap-1.5">
                            {parsePickupLocations(myRideRequest.pickup_area).map((loc, i) => (
                              <Badge key={i} variant="outline" className="bg-background/50 text-xs">
                                <MapPin className="w-3 h-3 mr-1" />
                                {loc}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Passengers:</span>
                          <span>{myRideRequest.passenger_count}</span>
                        </div>
                        {myRideRequest.preferred_date && (
                          <div className="flex justify-between text-sm" data-testid="my-request-when">
                            <span className="text-muted-foreground">When:</span>
                            <span className="font-medium">
                              {myRideRequest.preferred_date}{myRideRequest.preferred_time ? ` · ${myRideRequest.preferred_time}` : ''}
                            </span>
                          </div>
                        )}
                        {myRideRequest.notes && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Notes:</span>
                            <span className="text-right max-w-48 truncate">{myRideRequest.notes}</span>
                          </div>
                        )}
                        {myRideRequest.accepted_by_driver_name && (
                          <div className="flex justify-between text-sm border-t border-border/50 pt-2 mt-2">
                            <span className="text-muted-foreground">Driver:</span>
                            <span className="text-success">{handleWithFallback(myRideRequest.accepted_by_driver_username, myRideRequest.accepted_by_driver_name)}</span>
                          </div>
                        )}
                      </div>
                      
                      {myRideRequest.status === 'waiting' && (
                        <Button 
                          variant="outline" 
                          onClick={handleCancelRequest}
                          className="w-full rounded-full border-destructive/30 text-destructive hover:bg-destructive/10"
                          data-testid="cancel-request-btn"
                        >
                          Cancel Request
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center space-y-4">
                      <Users className="w-12 h-12 mx-auto text-muted-foreground" />
                      <div>
                        <p className="text-muted-foreground text-sm">
                          If no driver is available or the current pick up location is not suitable,<br />
                          you can request a ride and other drivers will see you're looking for a lift to this event.
                        </p>
                      </div>
                      <Button 
                        onClick={() => {
                          if (!user) {
                            toast.error('Please login to request a ride');
                            navigate('/login', { state: { from: `/events/${eventId}` } });
                            return;
                          }
                          setRequestStep(0); setRequestDialog(true);
                        }}
                        className="rounded-full bg-secondary hover:bg-secondary/90"
                        data-testid="request-ride-btn"
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Request a Ride
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

            {rideRequests.filter(r => r.status === 'waiting').length === 0 && (
              <p className="text-muted-foreground text-sm text-center py-2">
                No ride requests for this event yet
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Request Ride Dialog — Wizard Style */}
      <Dialog open={requestDialog} onOpenChange={(open) => { setRequestDialog(open); if (!open) setRequestStep(0); }}>
        <DialogContent className="bg-card border-border/50 max-w-md p-0 gap-0">
          {/* Progress bar */}
          <div className="px-5 pt-5 pb-0">
            <div className="flex items-center justify-between mb-3">
              {requestStep > 0 ? (
                <button onClick={() => setRequestStep(prev => prev - 1)}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="request-wizard-back">
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              ) : <div />}
              <span className="text-xs text-muted-foreground" data-testid="request-step-counter">
                {requestStep + 1} of 3
              </span>
            </div>
            <div className="flex gap-1.5 mb-4">
              {[0, 1, 2].map(i => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  i <= requestStep ? 'bg-primary' : 'bg-border'
                }`} />
              ))}
            </div>
          </div>

          {/* Step 1: Pickup Locations */}
          {requestStep === 0 && (
            <div className="px-5 pb-5 space-y-4">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-secondary/15 flex items-center justify-center mx-auto">
                  <MapPin className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="text-xl font-bold font-heading">Where from?</h3>
                <p className="text-sm text-muted-foreground">Where can a driver collect you?</p>
              </div>

              <div className="space-y-2">
                {requestForm.pickup_locations.map((loc, index) => (
                  <div key={index} className="flex items-center gap-2" data-testid={`pickup-location-${index}`}>
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                      {index + 1}
                    </div>
                    <Input
                      placeholder="e.g., Kings Cross Station"
                      value={loc}
                      onChange={(e) => {
                        const updated = [...requestForm.pickup_locations];
                        updated[index] = e.target.value;
                        setRequestForm({...requestForm, pickup_locations: updated});
                      }}
                      className="h-10 rounded-lg"
                      data-testid={`pickup-location-input-${index}`}
                    />
                    {requestForm.pickup_locations.length > 1 && (
                      <Button type="button" variant="ghost" size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0 px-2"
                        onClick={() => {
                          const updated = requestForm.pickup_locations.filter((_, i) => i !== index);
                          setRequestForm({...requestForm, pickup_locations: updated});
                        }}
                        data-testid={`remove-pickup-${index}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <Button type="button" variant="outline" size="sm"
                onClick={() => setRequestForm({...requestForm, pickup_locations: [...requestForm.pickup_locations, '']})}
                className="w-full rounded-full border-dashed border-primary/30 text-primary hover:bg-primary/5"
                data-testid="add-pickup-location-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Another Location
              </Button>

              {/* Quick add landmarks */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Quick add:</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Kings Cross', 'Victoria', 'Euston', 'Paddington', 'Stratford', 'Liverpool St'].map((landmark) => {
                    const alreadyAdded = requestForm.pickup_locations.some(l => l.trim().toLowerCase().includes(landmark.toLowerCase()));
                    return (
                      <Button key={landmark} type="button" variant="outline" size="sm"
                        className={`text-xs h-7 rounded-full ${alreadyAdded ? 'opacity-40 cursor-not-allowed' : ''}`}
                        disabled={alreadyAdded}
                        onClick={() => {
                          const emptyIndex = requestForm.pickup_locations.findIndex(l => !l.trim());
                          if (emptyIndex >= 0) {
                            const updated = [...requestForm.pickup_locations];
                            updated[emptyIndex] = `${landmark} Station`;
                            setRequestForm({...requestForm, pickup_locations: updated});
                          } else {
                            setRequestForm({...requestForm, pickup_locations: [...requestForm.pickup_locations, `${landmark} Station`]});
                          }
                        }}>
                        + {landmark}
                      </Button>
                    );
                  })}
                </div>
              </div>

              <Button onClick={() => {
                  if (!requestForm.pickup_locations.some(l => l.trim())) {
                    toast.error('Please add at least one pickup location');
                    return;
                  }
                  setRequestStep(1);
                }}
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-bold"
                data-testid="request-wizard-next">
                Next <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          )}

          {/* Step 2: How Many People */}
          {requestStep === 1 && (
            <div className="px-5 pb-5 space-y-6">
              {isVenue && (
                <div className="space-y-3 p-4 rounded-xl bg-secondary/10 border border-secondary/30" data-testid="venue-trip-datetime">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-secondary flex-shrink-0" />
                    <p className="text-sm font-semibold">When do you want to go?</p>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-2">
                    {event?.name} is open daily — pick the date and time that works for you.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Date</Label>
                      <Input
                        type="date"
                        value={requestForm.preferred_date}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setRequestForm({ ...requestForm, preferred_date: e.target.value })}
                        className="h-10 rounded-lg"
                        data-testid="venue-preferred-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Time (optional)</Label>
                      <Input
                        type="time"
                        value={requestForm.preferred_time}
                        onChange={(e) => setRequestForm({ ...requestForm, preferred_time: e.target.value })}
                        className="h-10 rounded-lg"
                        data-testid="venue-preferred-time"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mx-auto">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold font-heading">How many people?</h3>
                <p className="text-sm text-muted-foreground">Including yourself</p>
              </div>
              {/* Leg picker for the ride request — round trip vs one-way only.
                  Drivers will see this on the request and price accordingly. */}
              <div className="space-y-2" data-testid="request-leg-picker">
                <Label className="text-xs font-semibold text-muted-foreground">Riding both ways or one-way?</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {v:'round_trip', l:'Round trip'},
                    {v:'outbound',   l:'Outbound only'},
                    {v:'return',     l:'Return only'},
                  ].map(o => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setRequestForm(prev => ({ ...prev, leg: o.v }))}
                      className={`p-2 rounded-xl border-2 text-xs font-medium transition-all ${
                        requestForm.leg === o.v
                          ? 'border-secondary bg-secondary/15 text-secondary'
                          : 'border-border/40 bg-background/40 text-muted-foreground'
                      }`}
                      data-testid={`request-leg-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
                {requestForm.leg !== 'round_trip' && (
                  <p className="text-[11px] text-muted-foreground">
                    One-way bookings cost half the driver's per-seat price (50% of the round-trip rate).
                  </p>
                )}
              </div>

              <div className="flex flex-wrap justify-center gap-3">
                {[1, 2, 3, 4].map((num) => (
                  <button key={num} type="button"
                    onClick={() => setRequestForm({...requestForm, passenger_count: num})}
                    data-testid={`request-passenger-btn-${num}`}
                    className={`w-14 h-14 rounded-full text-xl font-bold transition-all duration-200 ${
                      requestForm.passenger_count === num
                        ? 'bg-primary text-primary-foreground scale-110 ring-2 ring-primary ring-offset-2 ring-offset-background'
                        : 'bg-card border-2 border-border hover:border-primary/50'
                    }`}>
                    {num}
                  </button>
                ))}
              </div>

              <p className="text-center text-lg font-medium">
                {requestForm.passenger_count} {requestForm.passenger_count === 1 ? 'person' : 'people'}
              </p>

              <Button onClick={() => {
                  if (isVenue && !requestForm.preferred_date) {
                    toast.error('Please pick the date you want to go');
                    return;
                  }
                  setRequestStep(2);
                }}
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-bold"
                data-testid="request-wizard-next-2">
                Next <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          )}

          {/* Step 3: Review + Notes */}
          {requestStep === 2 && (
            <div className="px-5 pb-5 space-y-4">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center mx-auto">
                  <BadgeCheck className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-xl font-bold font-heading">Review</h3>
              </div>

              {/* Summary cards */}
              <div className="space-y-2">
                <button type="button" onClick={() => setRequestStep(0)}
                  className="w-full p-3 rounded-xl bg-background/50 border border-border flex items-center justify-between text-left hover:bg-background/80 transition-colors"
                  data-testid="request-review-pickup">
                  <div className="flex items-center gap-3 min-w-0">
                    <MapPin className="w-4 h-4 text-secondary flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Pickup</p>
                      <p className="text-sm font-medium truncate">
                        {requestForm.pickup_locations.filter(l => l.trim()).join(', ') || 'Not set'}
                      </p>
                    </div>
                  </div>
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                </button>

                <button type="button" onClick={() => setRequestStep(1)}
                  className="w-full p-3 rounded-xl bg-background/50 border border-border flex items-center justify-between text-left hover:bg-background/80 transition-colors"
                  data-testid="request-review-passengers">
                  <div className="flex items-center gap-3">
                    <Users className="w-4 h-4 text-primary flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Passengers</p>
                      <p className="text-sm font-medium">{requestForm.passenger_count} {requestForm.passenger_count === 1 ? 'person' : 'people'}</p>
                    </div>
                  </div>
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
                {isVenue && (
                  <button type="button" onClick={() => setRequestStep(1)}
                    className="w-full p-3 rounded-xl bg-background/50 border border-border flex items-center justify-between text-left hover:bg-background/80 transition-colors"
                    data-testid="request-review-venue-date">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-secondary flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">When</p>
                        <p className="text-sm font-medium">
                          {requestForm.preferred_date || 'Not set'}{requestForm.preferred_time ? ` · ${requestForm.preferred_time}` : ''}
                        </p>
                      </div>
                    </div>
                    <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Notes for drivers (optional)</Label>
                <Textarea
                  placeholder="e.g., Available from 5pm, happy to meet at a tube station..."
                  value={requestForm.notes}
                  onChange={(e) => setRequestForm({...requestForm, notes: e.target.value})}
                  rows={2}
                  className="resize-none rounded-xl bg-background/50"
                  data-testid="request-notes-input"
                />
              </div>

              <Button
                onClick={handleRequestRide}
                disabled={requestLoading || !requestForm.pickup_locations.some(l => l.trim()) || (isVenue && !requestForm.preferred_date)}
                className="w-full h-12 rounded-full bg-accent hover:bg-accent/90 font-bold"
                data-testid="submit-request-btn">
                {requestLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Request
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Accept Request Dialog */}
      <Dialog open={acceptDialog} onOpenChange={setAcceptDialog}>
        <DialogContent className="bg-card border-border/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-semibold text-xl">Accept Ride Request</DialogTitle>
            <DialogDescription>
              Choose which of your trips to assign this rider to
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="p-4 rounded-lg bg-background/50 space-y-2">
                <p className="font-medium">{handle(selectedRequest, 'rider')}</p>
                <p className="text-sm text-muted-foreground">{selectedRequest.passenger_count} seat(s) needed</p>
              </div>

              {(() => {
                const locations = parsePickupLocations(selectedRequest.pickup_area);
                const driverHasTrips = event?.trips?.some(t => t.driver_id === user?.user_id);

                if (!driverHasTrips && locations.length > 0) {
                  // NO TRIPS — checkboxes to pick which locations to add to the new trip
                  return (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-accent" />
                        Which pickup points can you do?
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Select the locations you can collect from. These will be added to your trip.
                      </p>
                      <div className="space-y-2">
                        {locations.map((loc, idx) => (
                          <div key={idx} className="flex items-center space-x-3 p-3 rounded-lg border border-border/50 hover:border-accent/50 transition-colors bg-background/30">
                            <Checkbox
                              id={`check-pickup-${idx}`}
                              checked={checkedPickupsForTrip.includes(loc)}
                              onCheckedChange={(checked) => {
                                setCheckedPickupsForTrip(prev =>
                                  checked ? [...prev, loc] : prev.filter(l => l !== loc)
                                );
                              }}
                              data-testid={`check-pickup-${idx}`}
                            />
                            <Label htmlFor={`check-pickup-${idx}`} className="flex-1 cursor-pointer">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-3 h-3 text-muted-foreground" />
                                <span className="text-sm">{loc}</span>
                              </div>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // HAS TRIPS — radio buttons to pick one location for the booking
                if (locations.length > 1) {
                  return (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-accent" />
                        Where will you collect them?
                      </Label>
                      <RadioGroup
                        value={selectedPickupForAccept}
                        onValueChange={setSelectedPickupForAccept}
                      >
                        {locations.map((loc, idx) => (
                          <div key={idx} className="flex items-center space-x-3 p-3 rounded-lg border border-border/50 hover:border-accent/50 transition-colors bg-background/30">
                            <RadioGroupItem value={loc} id={`accept-pickup-${idx}`} />
                            <Label htmlFor={`accept-pickup-${idx}`} className="flex-1 cursor-pointer">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-3 h-3 text-muted-foreground" />
                                <span className="text-sm">{loc}</span>
                              </div>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  );
                }

                // Single location — just display it
                return (
                  <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-accent" />
                      <span>Pickup: <strong>{locations[0] || selectedRequest.pickup_area}</strong></span>
                    </div>
                  </div>
                );
              })()}
              
              {(() => {
                const driverTrips = event?.trips?.filter(t => t.driver_id === user?.user_id) || [];
                
                if (driverTrips.length === 0) {
                  // NO TRIPS — show "Create Trip" as the main action, skip trip selection
                  return null;
                }
                
                // HAS TRIPS — show trip selection
                return (
                  <div className="space-y-2">
                    <Label>Select Your Trip</Label>
                    <RadioGroup
                      value={selectedTripForAccept}
                      onValueChange={setSelectedTripForAccept}
                    >
                      {driverTrips.map((trip) => (
                        <div key={trip.trip_id} className="flex items-center space-x-3 p-3 rounded-lg bg-background/50">
                          <RadioGroupItem value={trip.trip_id} id={trip.trip_id} />
                          <Label htmlFor={trip.trip_id} className="flex-1 cursor-pointer">
                            <div className="flex justify-between">
                              <span>{trip.pickup_points?.[0]?.time_type === 'after' ? `After ${trip.pickup_points?.[0]?.time}` : trip.pickup_points?.[0]?.time} departure</span>
                              <Badge variant="outline">
                                {trip.available_seats} seats left
                              </Badge>
                            </div>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                );
              })()}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-col gap-2">
            {(() => {
              const driverTrips = event?.trips?.filter(t => t.driver_id === user?.user_id) || [];
              
              if (driverTrips.length === 0) {
                // No trips — "Create Trip" is the primary action
                return (
                  <>
                    <Button
                      onClick={() => {
                        if (checkedPickupsForTrip.length === 0) {
                          toast.error('Select at least one pickup location first');
                          return;
                        }
                        navigate('/create-trip', { 
                          state: { 
                            eventId: event.event_id, 
                            eventName: event.name,
                            returnRequestId: selectedRequest?.request_id,
                            requiredPickupLocations: checkedPickupsForTrip,
                            passengerName: selectedRequest ? handle(selectedRequest, 'rider') : '',
                            passengerCount: selectedRequest?.passenger_count
                          } 
                        });
                      }}
                      className="w-full rounded-full bg-primary hover:bg-primary/90"
                      disabled={checkedPickupsForTrip.length === 0}
                      data-testid="create-trip-from-request-btn"
                    >
                      <Car className="w-4 h-4 mr-2" />
                      Create Trip with {checkedPickupsForTrip.length || 0} Pickup{checkedPickupsForTrip.length !== 1 ? 's' : ''}
                    </Button>
                    <Button variant="outline" onClick={() => setAcceptDialog(false)} className="w-full rounded-full">
                      Cancel
                    </Button>
                  </>
                );
              }
              
              // Has trips — "Accept & Assign" is the primary action
              return (
                <>
                  <Button 
                    onClick={handleAcceptRequest} 
                    disabled={!selectedTripForAccept || (selectedRequest && parsePickupLocations(selectedRequest.pickup_area).length > 1 && !selectedPickupForAccept)}
                    className="w-full rounded-full bg-success hover:bg-success/90"
                    data-testid="confirm-accept-btn"
                  >
                    Accept & Assign
                  </Button>
                  <Button variant="outline" onClick={() => setAcceptDialog(false)} className="w-full rounded-full">
                    Cancel
                  </Button>
                </>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept Success Dialog — shown to driver after accepting */}
      <Dialog open={showAcceptSuccess} onOpenChange={setShowAcceptSuccess}>
        <DialogContent className="bg-card border-border/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <CheckCircle className="w-5 h-5 text-success" />
              Ride Request Accepted
            </DialogTitle>
            <DialogDescription className="text-left pt-2 space-y-3" asChild>
              <div>
                <p>You've accepted {acceptedRiderName}'s ride request.</p>
                <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-foreground">
                      <strong>Awaiting payment</strong> &mdash; {acceptedRiderName} has been notified and will need to complete payment to confirm the booking. You'll be notified once payment is made.
                    </p>
                  </div>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="w-full rounded-full" onClick={() => setShowAcceptSuccess(false)} data-testid="accept-success-ok-btn">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Dialog */}
      <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
        <DialogContent className="bg-card border-border/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-semibold text-xl">Book Your Ride</DialogTitle>
            <DialogDescription>
              Choose your pickup point and trip type
            </DialogDescription>
          </DialogHeader>

          {selectedTrip && (
            <div className="space-y-6 py-4">
              {/* Driver Info - clickable to profile */}
              <div 
                className={`flex items-center gap-3 p-3 rounded-xl bg-background/50 transition-colors ${selectedTrip.driver_is_admin ? 'cursor-default' : 'cursor-pointer hover:bg-background/70'}`}
                onClick={() => !selectedTrip.driver_is_admin && navigate(`/profile/${selectedTrip.driver_id}`, { state: { returnTo: `/events/${event.event_id}` } })}
              >
                <Avatar className="w-12 h-12 border-2 border-primary/30">
                  {selectedTrip.driver_photo ? (
                    <AvatarImage src={selectedTrip.driver_photo} alt={handle(selectedTrip, 'driver')} />
                  ) : null}
                  <AvatarFallback className="bg-primary/20 text-primary">
                    {(selectedTrip.driver_username || selectedTrip.driver_name || '?')[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{handle(selectedTrip, 'driver')} <VerifiedBadge show={selectedTrip.driver_verified} size={14} /></p>
                  <div className="flex items-center gap-1">
                    {renderStars(selectedTrip.driver_rating || 0)}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
              </div>

              {/* Venue trip date — for permanent venues, the trip's actual
                  travel date is the driver's chosen venue_trip_date, NOT the
                  event's sentinel 2099. Surface it prominently so riders know
                  WHICH day they're booking. */}
              {selectedTrip?.is_venue_trip && selectedTrip?.venue_trip_date && (
                <div className="p-3 rounded-xl border border-cyan-400/40 bg-cyan-400/10" data-testid="booking-venue-date">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-cyan-300 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Travel date</p>
                      <p className="text-sm font-semibold text-cyan-300">
                        {new Date(selectedTrip.venue_trip_date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        {selectedTrip.venue_trip_time ? ` · ${selectedTrip.venue_trip_time}` : ''}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Collection Point(s) — with selection when multiple */}
              <div className="p-4 rounded-xl border border-border/50 space-y-3 bg-card" data-testid="pickup-location-alert">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-accent flex-shrink-0" />
                  <p className="font-semibold text-sm text-foreground">Collection Point{selectedTrip.pickup_points?.length > 1 ? 's' : ''}</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  {selectedTrip.pickup_points?.length > 1 
                    ? 'Select the pickup location that works best for you'
                    : 'Please confirm the pickup location works for you before booking'}
                </p>
                
                {selectedTrip.pickup_points?.length > 1 ? (
                  <RadioGroup
                    value={String(selectedPickup)}
                    onValueChange={(v) => setSelectedPickup(parseInt(v))}
                  >
                    {selectedTrip.pickup_points.map((point, idx) => (
                      <div key={idx} className="flex items-center space-x-3 p-3 border border-border/50 hover:border-primary/50 transition-colors bg-muted/30">
                        <RadioGroupItem value={String(idx)} id={`pickup-${idx}`} />
                        <Label htmlFor={`pickup-${idx}`} className="flex-1 cursor-pointer">
                          <p className="font-medium text-sm text-foreground">{point.address}</p>
                          <p className="text-xs text-muted-foreground">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {point.time_type === 'after' ? `After ${point.time}` : point.time}
                          </p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                ) : (
                  selectedTrip.pickup_points?.map((point, idx) => (
                    <div key={idx} className="pl-7">
                      <p className="font-medium text-sm text-foreground">{point.address}</p>
                      <p className="text-xs text-muted-foreground">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {point.time_type === 'after' ? `After ${point.time}` : point.time}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {/* Leg picker — Round trip / Outbound / Return only.
                  One-way bookings cost 50% of the round-trip price (the driver
                  may drive the empty leg alone — petrol contribution model). */}
              {(() => {
                const av = tripAvailability;
                const baseRound = Number(selectedTrip?.contribution_amount || av?.round_trip_price_per_seat || 0);
                const baseOneWay = av?.one_way_price_per_seat ?? Math.round(baseRound * 0.5 * 10) / 10;
                const roundLeft = av ? av.round_trip_seats_left : null;
                const outLeft = av ? av.outbound_seats_left : null;
                const retLeft = av ? av.return_seats_left : null;
                const opt = (val, title, sub, price, left) => {
                  const disabled = av ? (left !== null && left <= 0) : false;
                  const active = selectedLeg === val;
                  // Pick the relevant distance for this leg so the rider sees
                  // exactly what miles their price covers.
                  const legMiles = val === 'round_trip'
                    ? selectedTrip?.total_distance_miles
                    : selectedTrip?.one_way_distance_miles;
                  return (
                    <button
                      type="button"
                      key={val}
                      disabled={disabled}
                      onClick={() => !disabled && setSelectedLeg(val)}
                      className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                        active
                          ? 'border-secondary bg-secondary/15 ring-1 ring-secondary'
                          : disabled
                            ? 'border-border/30 bg-muted/30 opacity-50 cursor-not-allowed'
                            : 'border-border/50 bg-background/40 hover:border-secondary/60'
                      }`}
                      data-testid={`leg-option-${val}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold">{title}</p>
                            {legMiles && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-1.5 py-0.5 leading-none">
                                <Navigation className="w-2.5 h-2.5" />
                                {legMiles} mi
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{sub}</p>
                          {av && (
                            <p className={`text-xs mt-0.5 ${left <= 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                              {left <= 0 ? 'No seats left' : `${left} seat${left === 1 ? '' : 's'} left`}
                            </p>
                          )}
                        </div>
                        <div className={`text-sm font-bold ${active ? 'text-secondary' : ''}`}>
                          £{Number(price || 0).toFixed(2)}
                        </div>
                      </div>
                    </button>
                  );
                };
                return (
                  <div className="space-y-2" data-testid="leg-picker">
                    <Label className="text-sm font-semibold">Riding both ways or one-way?</Label>
                    {opt('round_trip', 'Round trip', 'Outbound + return — full price', baseRound, roundLeft)}
                    {opt('outbound', 'One-way · Outbound only', "I'll make my own way back", baseOneWay, outLeft)}
                    {opt('return', 'One-way · Return only', "I'll get there another way", baseOneWay, retLeft)}
                  </div>
                );
              })()}

              <p className="text-xs text-center" data-testid="trip-pricing-helper">
                {selectedTrip?.contribution_amount !== null && selectedTrip?.contribution_amount !== undefined && Number(selectedTrip.contribution_amount) === 0
                  ? <span className="text-emerald-400 font-medium">This driver is offering this trip for free 🙌 No platform fee, no payment screen.</span>
                  : selectedTrip?.contribution_amount
                    ? <span className="text-muted-foreground">Driver set £{Number(selectedTrip.contribution_amount).toFixed(2)}/seat for the round trip · one-way is half. Platform fee applies.</span>
                    : <span className="text-muted-foreground">Petrol contribution will be calculated after booking</span>
                }
              </p>

              {/* Pickup Confirmation Checkbox */}
              <div className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-muted/30">
                <Checkbox
                  id="pickup-confirm"
                  checked={pickupConfirmed}
                  onCheckedChange={setPickupConfirmed}
                  data-testid="pickup-confirm-checkbox"
                  className="mt-0.5"
                />
                <label htmlFor="pickup-confirm" className="text-sm text-muted-foreground cursor-pointer leading-snug">
                  I confirm the pickup location and time work for me
                </label>
              </div>
            </div>
          )}

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setBookingDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmBooking}
              className="bg-primary hover:bg-primary/90"
              disabled={bookingLoading || !pickupConfirmed}
              data-testid="confirm-booking-btn"
            >
              {bookingLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Booking...
                </>
              ) : (
                'Confirm Booking'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-card border-border/50 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-semibold text-xl">Edit Event</DialogTitle>
            <DialogDescription>
              Update your event details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Event Name *</Label>
              <Input
                placeholder="Event name"
                className="h-11 rounded-lg"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Event description"
                className="min-h-20 rounded-lg"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-11 justify-start text-left font-normal rounded-lg",
                        !editForm.date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{editForm.date ? format(editForm.date, "PPP") : "Pick date"}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-card border-border/50" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={editForm.date}
                      onSelect={(date) => setEditForm({ ...editForm, date })}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Time *</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="time"
                    className="pl-10 h-11 rounded-lg w-full"
                    value={editForm.time}
                    onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Location *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Venue address"
                  className="pl-10 h-11 rounded-lg"
                  value={editForm.location.address}
                  onChange={(e) => setEditForm({ 
                    ...editForm, 
                    location: { ...editForm.location, address: e.target.value }
                  })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Event Thumbnail *</Label>
              <p className="text-xs text-muted-foreground">This image shows in event listings</p>
              <input
                ref={thumbnailInputRef}
                type="file" onClick={e => { e.target.value = null; }}
                accept="image/*"
                className="hidden"
                onChange={handleEditThumbnailUpload}
              />
              {editForm.image_url ? (
                <div className="space-y-3">
                  <div 
                    ref={thumbnailPreviewRef}
                    className="relative rounded-xl overflow-hidden border-2 border-border/50"
                    onMouseDown={() => setIsDragging(true)}
                    onMouseUp={() => setIsDragging(false)}
                    onMouseLeave={() => setIsDragging(false)}
                    onMouseMove={handleThumbnailDrag}
                    onTouchStart={() => setIsDragging(true)}
                    onTouchEnd={() => setIsDragging(false)}
                    onTouchMove={handleThumbnailTouchDrag}
                  >
                    <img loading="lazy" 
                      src={editForm.image_url} 
                      alt="Event thumbnail" 
                      className="w-full h-40 object-cover pointer-events-none select-none transition-transform"
                      style={{ 
                        objectPosition: `${editForm.thumbnail_position.x}% ${editForm.thumbnail_position.y}%`,
                        transform: `scale(${editForm.thumbnail_position.scale || 1})`
                      }}
                      draggable={false}
                    />
                    <div 
                      className="absolute w-3 h-3 bg-primary rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                      style={{ left: `${editForm.thumbnail_position.x}%`, top: `${editForm.thumbnail_position.y}%` }}
                    />
                  </div>
                  
                  {/* Position & Zoom Controls */}
                  <div className="bg-background/50 rounded-xl p-3 border border-border/50">
                    <p className="text-xs text-muted-foreground text-center mb-2">Adjust position and zoom</p>
                    <div className="flex items-center justify-center gap-6">
                      <div className="flex flex-col items-center gap-1">
                        <Button type="button" variant="outline" size="sm" onClick={() => movePosition('up')} className="w-9 h-9 p-0 rounded-lg">
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <div className="flex gap-1">
                          <Button type="button" variant="outline" size="sm" onClick={() => movePosition('left')} className="w-9 h-9 p-0 rounded-lg">
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={resetPosition} className="w-9 h-9 p-0 rounded-lg" title="Reset">
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => movePosition('right')} className="w-9 h-9 p-0 rounded-lg">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={() => movePosition('down')} className="w-9 h-9 p-0 rounded-lg">
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs text-muted-foreground">Zoom</span>
                        <Button type="button" variant="outline" size="sm" onClick={() => adjustScale('in')} className="w-9 h-9 p-0 rounded-lg" disabled={(editForm.thumbnail_position.scale || 1) >= 2}>
                          <ZoomIn className="w-4 h-4" />
                        </Button>
                        <span className="text-xs font-medium">{Math.round((editForm.thumbnail_position.scale || 1) * 100)}%</span>
                        <Button type="button" variant="outline" size="sm" onClick={() => adjustScale('out')} className="w-9 h-9 p-0 rounded-lg" disabled={(editForm.thumbnail_position.scale || 1) <= 0.5}>
                          <ZoomOut className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => thumbnailInputRef.current?.click()} disabled={thumbnailUploading} className="flex-1 rounded-xl" size="sm">
                      {thumbnailUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Change
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setEditForm(prev => ({ ...prev, image_url: '', thumbnail_position: { x: 50, y: 50, scale: 1 } }))} className="rounded-xl text-destructive hover:text-destructive" size="sm">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => !thumbnailUploading && thumbnailInputRef.current?.click()}
                  className={`border-2 border-dashed border-primary/40 rounded-lg p-4 text-center transition-colors ${thumbnailUploading ? 'cursor-wait bg-primary/5' : 'cursor-pointer hover:border-primary/50'}`}
                >
                  {thumbnailUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <p className="text-primary font-medium text-sm">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-6 h-6 text-muted-foreground" />
                      <p className="text-muted-foreground text-sm">Click to upload thumbnail</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Event Flyer *</Label>
              <p className="text-xs text-muted-foreground">Full-size flyer shown on event page</p>
              <input
                ref={flyerInputRef}
                type="file" onClick={e => { e.target.value = null; }}
                accept="image/*"
                className="hidden"
                onChange={handleEditFlyerUpload}
              />
              {editForm.flyer_url ? (
                <div className="relative">
                  <img loading="lazy" 
                    src={editForm.flyer_url} 
                    alt="Event flyer" 
                    className="w-full h-40 object-cover rounded-lg border border-border/50"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => flyerInputRef.current?.click()}
                      disabled={flyerUploading}
                    >
                      {flyerUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={() => setEditForm({ ...editForm, flyer_url: '' })}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => !flyerUploading && flyerInputRef.current?.click()}
                  className={`border-2 border-dashed border-primary/40 rounded-lg p-6 text-center transition-colors ${flyerUploading ? 'cursor-wait bg-primary/5' : 'cursor-pointer hover:border-primary/50'}`}
                >
                  {flyerUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <p className="text-primary font-medium">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <p className="text-muted-foreground">Click to upload flyer</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Ticket Link (optional)</Label>
              <p className="text-xs text-muted-foreground">Sell tickets through rollm8s or link to an external page</p>
              <div className="flex gap-2 mb-3">
                <Button
                  type="button"
                  size="sm"
                  variant={editForm.tickets_enabled ? "default" : "outline"}
                  className="flex-1 rounded-lg"
                  onClick={() => setEditForm({ ...editForm, tickets_enabled: true, ticket_link: '' })}
                  data-testid="edit-tickets-sell-btn"
                >
                  Sell Here
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={!editForm.tickets_enabled ? "default" : "outline"}
                  className="flex-1 rounded-lg"
                  onClick={() => setEditForm({ ...editForm, tickets_enabled: false, ticket_price: '', ticket_quantity: '' })}
                  data-testid="edit-tickets-link-btn"
                >
                  External Link
                </Button>
              </div>

              {editForm.tickets_enabled ? (
                <div className="space-y-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <p className="text-xs text-muted-foreground">
                    Sell tickets directly through rollm8s. Platform fee: £0.30 + 2% per ticket.
                  </p>
                  
                  {/* Single price mode */}
                  {editForm.ticket_releases.length === 0 && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Price (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.50"
                            placeholder="e.g. 12.50"
                            className="h-9 rounded-lg"
                            value={editForm.ticket_price}
                            onChange={(e) => setEditForm({ ...editForm, ticket_price: e.target.value })}
                            data-testid="edit-ticket-price-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Total Tickets</Label>
                          <Input
                            type="number"
                            min="1"
                            placeholder="e.g. 100"
                            className="h-9 rounded-lg"
                            value={editForm.ticket_quantity}
                            onChange={(e) => setEditForm({ ...editForm, ticket_quantity: e.target.value })}
                            data-testid="edit-ticket-quantity-input"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, ticket_releases: [
                          { name: 'Early Bird', price: editForm.ticket_price || '', quantity: '', end_date: '', sold: 0 },
                          { name: '2nd Release', price: '', quantity: '', end_date: '', sold: 0 }
                        ]})}
                        className="text-xs text-primary hover:underline"
                        data-testid="edit-add-releases-btn"
                      >
                        + Add ticket releases (Early Bird, 1st Release, etc.)
                      </button>
                    </>
                  )}

                  {/* Multi-release mode */}
                  {editForm.ticket_releases.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold">Ticket Releases</Label>
                        <button
                          type="button"
                          onClick={() => setEditForm({ ...editForm, ticket_releases: [], ticket_price: editForm.ticket_releases[0]?.price || '', ticket_quantity: '' })}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          Remove releases
                        </button>
                      </div>
                      {editForm.ticket_releases.map((release, idx) => (
                        <div key={idx} className="p-2 rounded-lg border border-border/40 bg-background/20 space-y-2" data-testid={`edit-release-card-${idx}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-primary">Release {idx + 1}{release.sold > 0 ? ` (${release.sold} sold)` : ''}</span>
                            {editForm.ticket_releases.length > 1 && (
                              <button type="button" onClick={() => {
                                const updated = editForm.ticket_releases.filter((_, i) => i !== idx);
                                setEditForm({ ...editForm, ticket_releases: updated });
                              }} className="text-[10px] text-destructive hover:underline">Remove</button>
                            )}
                          </div>
                          <Input placeholder="e.g. Early Bird" className="h-8 text-xs rounded-lg" value={release.name}
                            onChange={(e) => {
                              const updated = [...editForm.ticket_releases];
                              updated[idx] = { ...updated[idx], name: e.target.value };
                              setEditForm({ ...editForm, ticket_releases: updated });
                            }} data-testid={`edit-release-name-${idx}`} />
                          <div className="grid grid-cols-3 gap-1">
                            <div>
                              <Label className="text-[10px]">Price (£)</Label>
                              <Input type="number" step="0.01" min="0.50" placeholder="10" className="h-8 text-xs rounded-lg" value={release.price}
                                onChange={(e) => {
                                  const updated = [...editForm.ticket_releases];
                                  updated[idx] = { ...updated[idx], price: e.target.value };
                                  setEditForm({ ...editForm, ticket_releases: updated });
                                }} data-testid={`edit-release-price-${idx}`} />
                            </div>
                            <div>
                              <Label className="text-[10px]">Qty</Label>
                              <Input type="number" min="1" placeholder="50" className="h-8 text-xs rounded-lg" value={release.quantity}
                                onChange={(e) => {
                                  const updated = [...editForm.ticket_releases];
                                  updated[idx] = { ...updated[idx], quantity: e.target.value };
                                  setEditForm({ ...editForm, ticket_releases: updated });
                                }} data-testid={`edit-release-qty-${idx}`} />
                            </div>
                            <div>
                              <Label className="text-[10px]">Ends</Label>
                              <Input type="date" className="h-8 text-xs rounded-lg" value={release.end_date}
                                onChange={(e) => {
                                  const updated = [...editForm.ticket_releases];
                                  updated[idx] = { ...updated[idx], end_date: e.target.value };
                                  setEditForm({ ...editForm, ticket_releases: updated });
                                }} data-testid={`edit-release-end-${idx}`} />
                            </div>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={() => setEditForm({ ...editForm, ticket_releases: [...editForm.ticket_releases, { name: '', price: '', quantity: '', end_date: '', sold: 0 }] })}
                        className="w-full py-1.5 text-xs text-primary border border-dashed border-primary/30 rounded-lg hover:bg-primary/5"
                        data-testid="edit-add-another-release-btn">
                        + Add another release
                      </button>
                      <div className="text-xs text-muted-foreground bg-muted/20 p-2 rounded-lg">
                        Total: {editForm.ticket_releases.reduce((sum, r) => sum + (parseInt(r.quantity) || 0), 0)} tickets across {editForm.ticket_releases.length} releases
                      </div>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Max Per Buyer</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      placeholder="6"
                      className="h-9 rounded-lg"
                      value={editForm.max_per_buyer}
                      onChange={(e) => setEditForm({ ...editForm, max_per_buyer: e.target.value })}
                      data-testid="edit-max-per-buyer-input"
                    />
                  </div>

                  {/* Fee breakdown */}
                  {editForm.ticket_price && (
                    <div className="bg-muted/30 p-3 rounded-xl text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Ticket price</span>
                        <span>£{parseFloat(editForm.ticket_price).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Booking fee</span>
                        <span>£{(((editForm.booking_fee_override ? parseInt(editForm.booking_fee_fixed_pence) || 30 : 30) / 100) + parseFloat(editForm.ticket_price) * ((editForm.booking_fee_override ? parseFloat(editForm.booking_fee_percent) || 2 : 2) / 100)).toFixed(2)}</span>
                      </div>
                      {(() => {
                        const price = parseFloat(editForm.ticket_price) || 0;
                        const customPercent = editForm.booking_fee_override ? parseFloat(editForm.booking_fee_percent) || 2 : 2;
                        const customFixed = editForm.booking_fee_override ? parseInt(editForm.booking_fee_fixed_pence) || 30 : 30;
                        const platformMinPercent = 2;
                        const platformMinFixed = 30;
                        const totalFee = (customFixed / 100) + (price * customPercent / 100);
                        const platformMinFee = (platformMinFixed / 100) + (price * platformMinPercent / 100);
                        const organiserExtra = Math.max(0, totalFee - platformMinFee);
                        const buyerPaysFee = editForm.booking_fee_override ? editForm.fee_passed_to_buyer !== false : true;
                        const youReceive = buyerPaysFee ? price + organiserExtra : price - platformMinFee;
                        return (
                          <>
                            {organiserExtra > 0.001 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Platform min fee</span>
                                <span>£{platformMinFee.toFixed(2)}</span>
                              </div>
                            )}
                            {organiserExtra > 0.001 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Extra fee (yours)</span>
                                <span className="text-success">+£{organiserExtra.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-medium pt-1 border-t border-border/50">
                              <span>{buyerPaysFee ? 'You receive' : 'You receive (fee absorbed)'}</span>
                              <span className="text-success">£{youReceive.toFixed(2)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Fee Override Section */}
                  <div className="border-t border-border/30 pt-3 mt-1">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={editForm.booking_fee_override}
                        onChange={(e) => setEditForm({ ...editForm, booking_fee_override: e.target.checked, fee_passed_to_buyer: e.target.checked ? true : null })}
                        className="rounded"
                        data-testid="edit-fee-override-toggle"
                      />
                      <Label className="text-sm">Customise booking fee for this event</Label>
                    </div>
                    {editForm.booking_fee_override && (
                      <div className="space-y-2 pl-1 animate-fade-in">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Fee %</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              placeholder="2"
                              value={editForm.booking_fee_percent}
                              onChange={(e) => setEditForm({ ...editForm, booking_fee_percent: e.target.value })}
                              className="h-9 text-sm"
                              data-testid="edit-fee-percent"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Fixed fee (pence)</Label>
                            <Input
                              type="number"
                              min="0"
                              placeholder="30"
                              value={editForm.booking_fee_fixed_pence}
                              onChange={(e) => setEditForm({ ...editForm, booking_fee_fixed_pence: e.target.value })}
                              className="h-9 text-sm"
                              data-testid="edit-fee-fixed"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={editForm.fee_passed_to_buyer !== false}
                            onChange={(e) => setEditForm({ ...editForm, fee_passed_to_buyer: e.target.checked })}
                            className="rounded"
                            data-testid="edit-fee-buyer-pays"
                          />
                          <Label className="text-xs">Buyer pays booking fee on top</Label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Input
                  placeholder="https://eventbrite.co.uk/..."
                  className="h-11 rounded-lg"
                  value={editForm.ticket_link}
                  onChange={(e) => setEditForm({ ...editForm, ticket_link: e.target.value })}
                />
              )}
            </div>

            {/* Venue Info */}
            <div className="space-y-2 pt-2 border-t border-border/30">
              <Label className="text-xs font-semibold text-muted-foreground">Venue Info</Label>
              <div className="flex items-center justify-between py-1.5">
                <Label className="text-sm">ID Required</Label>
                <Switch checked={editForm.requires_id} onCheckedChange={(checked) => setEditForm({ ...editForm, requires_id: checked })} data-testid="edit-requires-id" />
              </div>
              <div className="flex items-center justify-between py-1.5">
                <Label className="text-sm">Fully Licensed Bar</Label>
                <Switch checked={editForm.has_bar} onCheckedChange={(checked) => setEditForm({ ...editForm, has_bar: checked })} data-testid="edit-has-bar" />
              </div>
              <div className="flex items-center justify-between py-1.5">
                <Label className="text-sm">Food Sold on Premises</Label>
                <Switch checked={editForm.has_food_vendors} onCheckedChange={(checked) => setEditForm({ ...editForm, has_food_vendors: checked })} data-testid="edit-has-food" />
              </div>
              <div className="flex items-center justify-between py-1.5">
                <Label className="text-sm">Toilets Available</Label>
                <Switch checked={editForm.has_toilets} onCheckedChange={(checked) => setEditForm({ ...editForm, has_toilets: checked })} data-testid="edit-has-toilets" />
              </div>
              <div className="flex items-center justify-between py-1.5">
                <Label className="text-sm">Skate Hire Available</Label>
                <Switch checked={editForm.has_skate_hire} onCheckedChange={(checked) => setEditForm({ ...editForm, has_skate_hire: checked })} data-testid="edit-has-skate-hire" />
              </div>
              <div className="pt-2">
                <Label className="text-xs font-medium mb-1 block">Nearest Station(s)</Label>
                {editForm.nearest_stations.map((station, idx) => (
                  <div key={idx} className="flex items-center gap-1 mb-1.5">
                    <Input placeholder="e.g. London Bridge" className="h-8 text-xs rounded-lg flex-1" value={station}
                      onChange={(e) => { const u = [...editForm.nearest_stations]; u[idx] = e.target.value; setEditForm({ ...editForm, nearest_stations: u }); }}
                      data-testid={`edit-station-${idx}`} />
                    {editForm.nearest_stations.length > 1 && (
                      <button type="button" onClick={() => setEditForm({ ...editForm, nearest_stations: editForm.nearest_stations.filter((_, i) => i !== idx) })}
                        className="text-destructive text-xs p-0.5">x</button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setEditForm({ ...editForm, nearest_stations: [...editForm.nearest_stations, ''] })}
                  className="text-[10px] text-primary hover:underline" data-testid="edit-add-station">+ Add station</button>
              </div>
            </div>

            {/* Parking Information */}
            <div className="space-y-4 pt-2 border-t border-border/30">
              <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                <div className="flex items-center gap-2">
                  <ParkingCircle className="w-5 h-5 text-primary" />
                  <div>
                    <Label className="text-sm">Onsite Parking</Label>
                    <p className="text-xs text-muted-foreground">Does the venue have parking?</p>
                  </div>
                </div>
                <Switch
                  checked={editForm.has_onsite_parking}
                  onCheckedChange={(checked) => setEditForm({ ...editForm, has_onsite_parking: checked })}
                />
              </div>
              
              {!editForm.has_onsite_parking && (
                <div className="space-y-2">
                  <Label>Alternative Parking (optional)</Label>
                  <Textarea
                    placeholder="e.g., Street parking, nearby NCP..."
                    className="min-h-16 rounded-lg"
                    value={editForm.alternative_parking}
                    onChange={(e) => setEditForm({ ...editForm, alternative_parking: e.target.value })}
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditEvent}
              className="bg-primary hover:bg-primary/90"
              disabled={editLoading}
              data-testid="save-event-btn"
            >
              {editLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Event Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-card border-border/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-semibold text-xl text-destructive">Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this event? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30">
              <p className="font-semibold text-destructive">{event?.name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {isVenue ? (event?.opening_hours || 'Open daily') : (event?.date && formatDate(event.date))}
              </p>
              {event?.trips?.length > 0 && (
                <p className="text-sm text-destructive mt-2">
                  ⚠️ This event has {event.trips.length} active trip(s). Deleting will also remove all associated trips.
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteEvent}
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleteLoading}
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Event
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Purchase Dialog */}
      <Dialog open={ticketDialog} onOpenChange={setTicketDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{(() => {
              const price = (() => {
                if (feeConfig?.releases_simultaneous && selectedReleaseIndex >= 0 && feeConfig?.releases?.[selectedReleaseIndex]) return feeConfig.releases[selectedReleaseIndex].price_pence;
                return feeConfig?.has_releases && feeConfig?.active_release ? feeConfig.active_release.price_pence : (event?.ticket_price_pence || 0);
              })();
              return price === 0 ? 'Get Free Tickets' : 'Buy Tickets';
            })()}</DialogTitle>
            <DialogDescription>
              {event?.name}
            </DialogDescription>
          </DialogHeader>
          {event?.tickets_enabled && (
            <div className="space-y-4 py-2">
              {/* Show all releases — sold out ones with strikethrough */}
              {feeConfig?.has_releases && feeConfig?.releases?.length > 0 && (
                <div className="space-y-1.5">
                  {feeConfig.releases_simultaneous && (
                    <p className="text-xs text-primary mb-1">Select your ticket type:</p>
                  )}
                  {feeConfig.releases.map((rel, idx) => {
                    const isSoldOut = rel.quantity > 0 && (rel.sold || 0) >= rel.quantity;
                    const endPassed = rel.end_date && new Date(rel.end_date) < new Date();
                    const isExhausted = isSoldOut || endPassed;
                    const isActive = feeConfig.releases_simultaneous
                      ? selectedReleaseIndex === idx
                      : idx === feeConfig.active_release_index;
                    const isSelectable = feeConfig.releases_simultaneous && !isExhausted;
                    const remaining = rel.quantity > 0 ? Math.max(0, rel.quantity - (rel.sold || 0)) : null;
                    const isFreeRelease = (rel.price_pence || 0) === 0;
                    return (
                      <div key={idx}
                        onClick={() => isSelectable && setSelectedReleaseIndex(idx)}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all ${
                          isActive ? 'bg-primary/10 border-primary/40 ring-1 ring-primary/30' :
                          isExhausted ? 'bg-muted/20 border-border/20 opacity-50' :
                          isSelectable ? 'border-border/30 hover:border-primary/30 cursor-pointer' :
                          'border-border/20'
                        }`}
                        data-testid={`release-option-${idx}`}>
                        <div className="flex items-center gap-2">
                          {isActive && <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
                          <div>
                            <span className={`text-sm font-medium ${isExhausted ? 'line-through text-muted-foreground/60' : isActive ? 'text-primary' : 'text-foreground'}`}>
                              {rel.name || `Release ${idx + 1}`}
                            </span>
                            {remaining !== null && !isExhausted && (
                              <span className="text-[10px] text-muted-foreground ml-2">{remaining} left</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${isExhausted ? 'line-through text-muted-foreground/60' : 'font-semibold'}`}>
                            {isFreeRelease ? 'FREE' : `£${(rel.price_pence / 100).toFixed(2)}`}
                          </span>
                          {isExhausted && <span className="text-[10px] text-destructive font-bold uppercase">Sold Out</span>}
                          {isActive && !feeConfig.releases_simultaneous && <span className="text-[10px] text-primary font-bold uppercase">On Sale</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Ticket price row */}
              {(() => {
                const activePrice = (() => {
                  if (feeConfig?.releases_simultaneous && selectedReleaseIndex >= 0 && feeConfig?.releases?.[selectedReleaseIndex]) return feeConfig.releases[selectedReleaseIndex].price_pence;
                  return feeConfig?.has_releases && feeConfig?.active_release ? feeConfig.active_release.price_pence : (event?.ticket_price_pence || 0);
                })();
                return (
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Ticket price</span>
                    <span className="font-semibold">{activePrice === 0 ? <span className="text-green-400">FREE</span> : `£${(activePrice / 100).toFixed(2)}`}</span>
                  </div>
                );
              })()}
              <div>
                <Label>Quantity</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Button variant="outline" size="icon" className="rounded-full" onClick={() => setTicketQuantity(Math.max(1, ticketQuantity - 1))} data-testid="ticket-qty-minus">-</Button>
                  <span className="text-xl font-bold w-8 text-center" data-testid="ticket-qty-display">{ticketQuantity}</span>
                  <Button variant="outline" size="icon" className="rounded-full" onClick={() => setTicketQuantity(Math.min(event.max_per_buyer || 6, ticketQuantity + 1))} data-testid="ticket-qty-plus">+</Button>
                  <span className="text-xs text-muted-foreground ml-2">max {event.max_per_buyer || 6}</span>
                </div>
              </div>
              {/* Price breakdown — only for paid tickets */}
              {(() => {
                const activePrice = (() => {
                  if (feeConfig?.releases_simultaneous && selectedReleaseIndex >= 0 && feeConfig?.releases?.[selectedReleaseIndex]) return feeConfig.releases[selectedReleaseIndex].price_pence;
                  return feeConfig?.has_releases && feeConfig?.active_release ? feeConfig.active_release.price_pence : (event?.ticket_price_pence || 0);
                })();
                if (activePrice === 0) return null;
                const subtotal = activePrice * ticketQuantity;
                const promoDiscount = promoApplied
                  ? promoApplied.discount_type === 'percent'
                    ? Math.round(subtotal * promoApplied.discount_value / 100)
                    : Math.min(promoApplied.discount_value * ticketQuantity, subtotal)
                  : 0;
                const subtotalAfterDiscount = subtotal - promoDiscount;
                const adjustedFee = feeConfig
                  ? Math.max(50, Math.round((feeConfig.fee_fixed_pence * ticketQuantity) + (subtotalAfterDiscount * feeConfig.fee_percent / 100)))
                  : 0;
                const total = feeConfig?.fee_passed_to_buyer ? subtotalAfterDiscount + adjustedFee : subtotalAfterDiscount;
                return (
                  <div className="bg-muted/30 p-3 rounded-xl text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{ticketQuantity} x £{(activePrice / 100).toFixed(2)}</span>
                      <span>£{(subtotal / 100).toFixed(2)}</span>
                    </div>
                    {promoDiscount > 0 && (
                      <div className="flex justify-between text-green-400">
                        <span>Discount ({promoApplied.code})</span>
                        <span>-£{(promoDiscount / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {feeConfig && feeConfig.fee_passed_to_buyer && adjustedFee > 0 && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Booking fee</span>
                        <span>£{(adjustedFee / 100).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium pt-1 border-t border-border/50">
                      <span>Total</span>
                      <span>£{(total / 100).toFixed(2)}</span>
                    </div>
                  </div>
                );
              })()}
              {/* Promo code input — only for paid tickets */}
              {(() => {
                const activePrice = (() => {
                  if (feeConfig?.releases_simultaneous && selectedReleaseIndex >= 0 && feeConfig?.releases?.[selectedReleaseIndex]) return feeConfig.releases[selectedReleaseIndex].price_pence;
                  return feeConfig?.has_releases && feeConfig?.active_release ? feeConfig.active_release.price_pence : (event?.ticket_price_pence || 0);
                })();
                if (activePrice === 0) return null;
                return (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Got a promo code?</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter code"
                        value={promoCode}
                        onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); }}
                        className="h-9 rounded-lg text-sm uppercase"
                        disabled={!!promoApplied}
                        data-testid="promo-code-input"
                      />
                      {promoApplied ? (
                        <Button size="sm" variant="ghost" className="rounded-lg text-xs text-destructive" onClick={() => { setPromoApplied(null); setPromoCode(''); }} data-testid="promo-remove-btn">
                          Remove
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={handleValidatePromo} disabled={promoLoading || !promoCode.trim()} data-testid="promo-apply-btn">
                          {promoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
                        </Button>
                      )}
                    </div>
                    {promoError && <p className="text-xs text-destructive">{promoError}</p>}
                    {promoApplied && <p className="text-xs text-green-400">Code "{promoApplied.code}" applied — {promoApplied.discount_type === 'percent' ? `${promoApplied.discount_value}% off` : `£${(promoApplied.discount_value / 100).toFixed(2)} off per ticket`}</p>}
                  </div>
                );
              })()}
            </div>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {(() => {
              const activePrice = (() => {
                if (feeConfig?.releases_simultaneous && selectedReleaseIndex >= 0 && feeConfig?.releases?.[selectedReleaseIndex]) return feeConfig.releases[selectedReleaseIndex].price_pence;
                return feeConfig?.has_releases && feeConfig?.active_release ? feeConfig.active_release.price_pence : (event?.ticket_price_pence || 0);
              })();
              const isFree = activePrice === 0;
              const cashEnabled = feeConfig?.cash_payment_enabled && !isFree;

              const handleFreeTicket = async () => {
                setTicketLoading(true);
                try {
                  const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                  const releaseParam = feeConfig?.releases_simultaneous && selectedReleaseIndex >= 0 ? `&release_index=${selectedReleaseIndex}` : '';
                  const res = await axios.post(`${API}/tickets/claim-free?event_id=${event.event_id}&quantity=${ticketQuantity}${releaseParam}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setTicketDialog(false);
                  setPurchaseConfirm({
                    open: true,
                    eventName: event.name,
                    ticketCount: res.data?.tickets?.length || ticketQuantity,
                    paymentType: 'free',
                  });
                  // Refresh event data
                  const ev = await axios.get(`${API}/events/${event.event_id}`);
                  if (ev.data) setEvent(ev.data);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to claim free tickets');
                } finally {
                  setTicketLoading(false);
                }
              };

              const handleCashTicket = async () => {
                setTicketLoading(true);
                try {
                  const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                  const releaseParam = feeConfig?.releases_simultaneous && selectedReleaseIndex >= 0 ? `&release_index=${selectedReleaseIndex}` : '';
                  const res = await axios.post(`${API}/tickets/claim-cash?event_id=${event.event_id}&quantity=${ticketQuantity}${releaseParam}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setTicketDialog(false);
                  setPurchaseConfirm({
                    open: true,
                    eventName: event.name,
                    ticketCount: res.data?.tickets?.length || ticketQuantity,
                    paymentType: 'cash',
                  });
                  const ev = await axios.get(`${API}/events/${event.event_id}`);
                  if (ev.data) setEvent(ev.data);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to reserve cash tickets');
                } finally {
                  setTicketLoading(false);
                }
              };

              const handleOnlineCheckout = async () => {
                setTicketLoading(true);
                try {
                  const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                  const releaseParam = feeConfig?.releases_simultaneous && selectedReleaseIndex >= 0 ? `&release_index=${selectedReleaseIndex}` : '';
                  const res = await axios.post(`${API}/tickets/checkout?event_id=${event.event_id}&quantity=${ticketQuantity}&origin_url=${encodeURIComponent(window.location.origin)}${promoApplied ? `&promo_code=${encodeURIComponent(promoApplied.code)}` : ''}${releaseParam}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  if (res.data.checkout_url) {
                    window.location.href = res.data.checkout_url;
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to start checkout');
                } finally {
                  setTicketLoading(false);
                }
              };

              if (isFree) {
                return (
                  <div className="flex gap-2 w-full">
                    <Button variant="outline" onClick={() => setTicketDialog(false)} className="rounded-full flex-1">Cancel</Button>
                    <Button className="rounded-full flex-1 bg-green-600 hover:bg-green-700" disabled={ticketLoading} onClick={handleFreeTicket} data-testid="ticket-claim-free-btn">
                      {ticketLoading ? 'Processing...' : `Get ${ticketQuantity > 1 ? ticketQuantity + ' ' : ''}Free Ticket${ticketQuantity > 1 ? 's' : ''}`}
                    </Button>
                  </div>
                );
              }

              if (cashEnabled) {
                return (
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex gap-2 w-full">
                      <Button className="rounded-full flex-1" disabled={ticketLoading} onClick={handleOnlineCheckout} data-testid="ticket-checkout-btn">
                        {ticketLoading ? 'Processing...' : 'Pay Online'}
                      </Button>
                      <Button variant="outline" className="rounded-full flex-1 border-amber-500/40 text-amber-400 hover:bg-amber-500/10" disabled={ticketLoading} onClick={handleCashTicket} data-testid="ticket-cash-btn">
                        {ticketLoading ? 'Processing...' : 'Pay Cash at Door'}
                      </Button>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setTicketDialog(false)} className="rounded-full text-xs text-muted-foreground">Cancel</Button>
                  </div>
                );
              }

              // Default: online only
              return (
                <div className="flex gap-2 w-full">
                  <Button variant="outline" onClick={() => setTicketDialog(false)} className="rounded-full flex-1">Cancel</Button>
                  <Button className="rounded-full flex-1" disabled={ticketLoading} onClick={handleOnlineCheckout} data-testid="ticket-checkout-btn">
                    {ticketLoading ? 'Processing...' : `Pay £${((() => {
                      const subtotal = activePrice * ticketQuantity;
                      if (!feeConfig?.fee_passed_to_buyer) return subtotal;
                      const fee = Math.max(50, Math.round((feeConfig.fee_fixed_pence * ticketQuantity) + (subtotal * feeConfig.fee_percent / 100)));
                      return subtotal + fee;
                    })() / 100).toFixed(2)}`}
                  </Button>
                </div>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendee List + Analytics Dialog */}
      <Dialog open={attendeeDialog} onOpenChange={setAttendeeDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-secondary" />
              Attendees & Analytics
            </DialogTitle>
          </DialogHeader>
          {analyticsData && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-muted/30 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-primary">{analyticsData.views || 0}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Views</p>
              </div>
              <div className="bg-muted/30 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-secondary">{analyticsData.tickets_sold || 0}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Sold</p>
              </div>
              <div className="bg-muted/30 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-foreground">{analyticsData.conversion_rate || 0}%</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Conversion</p>
              </div>
            </div>
          )}
          {attendeeList && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {attendeeList.total_tickets} ticket{attendeeList.total_tickets !== 1 ? 's' : ''} · {attendeeList.total_checked_in} checked in
                </p>
                <Button size="sm" variant="outline" className="rounded-full text-xs gap-1" onClick={handleExportCSV} data-testid="export-csv-btn">
                  <ExternalLink className="w-3 h-3" />
                  Export CSV
                </Button>
              </div>
              {attendeeList.attendees?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No tickets sold yet</p>
              ) : (
                <div className="border border-border/30 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-muted/20 text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                    <span className="col-span-3">Name</span>
                    <span className="col-span-3">Email</span>
                    <span className="col-span-2 text-center">Qty</span>
                    <span className="col-span-2 text-center">Payment</span>
                    <span className="col-span-2 text-center">Status</span>
                  </div>
                  {attendeeList.attendees.map((a, i) => (
                    <div key={a.ticket_id || i} className="grid grid-cols-12 gap-1 px-3 py-2 text-xs border-t border-border/20 hover:bg-muted/10">
                      <span className="col-span-3 truncate">{a.buyer_name}</span>
                      <span className="col-span-3 truncate text-muted-foreground">{a.buyer_email}</span>
                      <span className="col-span-2 text-center">{a.quantity || 1}</span>
                      <span className="col-span-2 text-center">
                        {a.payment_type === 'free' ? (
                          <Badge className="bg-green-500/20 text-green-400 text-[10px]">Free</Badge>
                        ) : a.payment_type === 'cash' ? (
                          <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Cash</Badge>
                        ) : (
                          <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">Paid</Badge>
                        )}
                      </span>
                      <span className="col-span-2 text-center">
                        {a.checked_in ? (
                          <Badge className="bg-success/20 text-success text-[10px]">In</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Pending</Badge>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!attendeeList && !analyticsData && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Open Group Chat via context (auto-opens on page visit) */}
      <StripeOnboardingConfirmDialog
        open={showOrganiserStripeConfirm}
        onOpenChange={setShowOrganiserStripeConfirm}
        onConfirm={startOrganiserStripeOnboarding}
        loading={stripeConnectLoading}
        audience="organiser"
      />
    </div>
  );
};

export default EventDetailsPage;
